import type { Dictionary } from '../types'

const dict: Dictionary = {
  common: {
    save: '保存',
    saving: '保存中...',
    saved: '已保存',
    cancel: '取消',
    delete: '删除',
    confirm: '确认',
    close: '关闭',
    back: '返回',
    next: '下一步',
    done: '完成',
    retry: '重试',
    add: '新增',
    edit: '编辑',
    update: '更新',
    loading: '加载中...',
    search: '搜索',
    filter: '筛选',
    sort: '排序',
    all: '全部',
    yes: '是',
    no: '否',
    enable: '启用',
    disable: '停用',
    enabled: '已启用',
    disabled: '已停用',
    optional: '选填',
    unlimited: '无限',
    unknown: '未知',
    unknownCustomer: '未知客户',
    noData: '暂无数据',
    comingSoon: '即将推出',
    export: '导出',
    signOut: '退出登录',
    admin: '管理员',
    owner: '店主',
    staff: '员工',
    manager: '经理',
    agent: '客服',
    marketer: '推广',
    finance: '财务',
    viewer: '浏览者',
    viewAll: '查看全部',
    today: '今天',
    more: '更多',
  },

  status: {
    pending: '待处理',
    confirmed: '已确认',
    paid: '已付款',
    completed: '已完成',
    cancelled: '已取消',
    noShow: '未到',
    inProgress: '处理中',
    resolved: '已解决',
    closed: '已关闭',
    listed: '已上架',
    unlisted: '已下架',
    unpaid: '未付款',
  },

  nav: {
    overview: '概览',
    conversations: '对话',
    orders: '订单',
    bookings: '预约',
    bookingsFnb: '订位',
    customers: '客户',
    products: '产品',
    productsBooking: '服务',
    productsConsultation: '咨询项目',
    productsSupport: '支援项目',
    catalog: '目录',
    inquiries: '咨询',
    followUps: '跟进',
    knowledge: '知识库',
    loyalty: '会员',
    coupons: '优惠券',
    broadcasts: '群发',
    agent: 'AI 客服',
    sandbox: '测试对话',
    sandboxMode: '测试模式',
    storefront: '网店',
    insights: '分析',
    settings: '设置',
    usage: '用量',
  },

  switcher: {
    needsAttentionPlural: '{count} 个其他品牌需要处理',
    needsAttentionSingular: '另一个品牌需要处理',
    allClear: '其他品牌已清',
    triggerAriaLabel: '切换品牌',
    listboxAriaLabel: '可切换的品牌',
    loading: '加载品牌中…',
    switchError: '切换品牌失败，请重试',
    notAccessibleError: '没有权限访问该品牌',
    networkError: '网络连接失败',
    unreadBadgeSingular: '{count} 条未处理',
    unreadBadgePlural: '{count} 条未处理',
  },

  overview: {
    title: '概览',
    subtitle: '您的 AI 助手全天候帮您处理客户对话',
    subtitleBooking: 'AI 24×7 接客 · 预约 · 跟进',
    subtitleBookingFnb: 'AI 24×7 接客 · 订位 · 跟进',
    subtitleConsultation: 'AI 24×7 接收咨询 · 分流 · 整理客户资料',
    subtitleSupport: 'AI 24×7 回答常见问题 · 开工单 · 自助分流',
    conversations: '对话',
    orders: '订单',
    bookings: '预约',
    bookingsFnb: '订位',
    inquiriesNew: '新咨询',
    inquiriesPending: '待处理',
    revenue: '收入',
    monthlyUsage: '本月 AI credits',
    percentUsed: '% 已使用',
    activeConversations: '进行中对话：{count} 个',
    viewAllConversations: '查看所有对话 →',
    topCustomers: '顶级客户',
    viewAll: '查看全部',
    aiValueOrders: 'AI 带来的订单',
    aiValueInquiries: 'AI 处理咨询',
    aiValueInquiriesSub: '无需转人工',
  },

  products: {
    title: '产品',
    addProduct: '新增产品 / 服务',
    searchPlaceholder: '搜索产品名称...',
    noProducts: '暂无产品',
    noProductsHint: '请新增产品或服务',
    seedSamples: {
      button: '试试示例产品',
      loading: '载入中…',
      hint: '载入一组小型示例产品，您可以随时修改或删除。',
      failed: '载入示例失败',
    },
    product: '产品',
    service: '服务',
    inquiry: '咨询',
    package: '套票',
    packageTypeOption: '套票（多次预约）',
    packageHint: '客户预付以兑换 N 次指定服务预约，通常比逐次下单便宜。',
    packageTargetService: '对应服务',
    packageTargetServiceHint: '每次兑换时会针对此服务创建一个预约',
    packageSelectTargetService: '请选择',
    packageTotalSessions: '总次数',
    packageTotalSessionsPlaceholder: '例如 6',
    packageValidityDays: '有效期（天）',
    packageValidityDaysPlaceholder: '例如 365（留空 = 永久有效）',
    packageValidityLabel: '有效期',
    packageValidityForever: '永久有效',
    packageValidityDaysFormat: '{days} 天',
    packageSessionsShort: '{count} 次',
    packageRowSessions: '× {count} 次',
    basicInfo: '基本信息',
    name: '名称',
    price: '价格',
    pricePlaceholder: '例：280',
    description: '描述',
    descriptionPlaceholder: '简单描述您的产品或服务...',
    salesSettings: '销售设置',
    orderingMode: '下单模式',
    directOrder: '直接下单',
    inquiryOnly: '仅供咨询',
    fulfillment: '取货方式',
    delivery: '配送',
    pickup: '自提',
    deliveryOrPickup: '配送或自提',
    na: '不适用',
    serviceMode: '服务方式',
    inStore: '到店',
    atCustomer: '上门',
    online: '线上',
    hybrid: '灵活',
    paymentTiming: '付款时机',
    fullUpfront: '全额付款',
    deposit: '定金',
    onCompletion: '完成后付',
    requiresBooking: '需要预约',
    requiresBookingYes: '客户需要选择日期时间',
    requiresBookingNo: '客户可以直接下单，无需选择时间',
    depositPercent: '定金百分比 (%)',
    depositPercentPlaceholder: '例：30',
    priceRange: '价格范围',
    minPrice: '最低价',
    maxPrice: '最高价',
    quotationNotes: '报价备注（AI 用）',
    quotationNotesPlaceholder: '例：价格视面积、楼层、工程复杂度而定',
    priceRangeHint: '设定价格范围后，AI 会主动告知客户大约价格。报价备注会帮助 AI 收集相关信息。',
    pickupAddress: '自提地址',
    pickupAddressPlaceholder: '例：旺角弥敦道123号',
    pickupNotes: '自提备注',
    pickupNotesPlaceholder: '例：请出示订单编号',
    serviceAddress: '服务地址',
    serviceAddressPlaceholder: '例：铜锣湾记利佐治街1号',
    serviceDuration: '服务时长（分钟）',
    serviceDurationPlaceholder: '例：60',
    concurrent: '同时服务人数',
    concurrentDefault: '默认：1',
    creating: '创建中...',
    confirmAdd: '确认新增',
    draftBanner: '该{type}未上架，客户暂时无法看到。准备好后点击"已下架"按钮上架。',
    recentOrders: '近期订单',
    variants: '规格',
    variantName: '规格名称',
    variantNamePlaceholder: '例：白色 / M',
    variantPrice: '价格（留空 = 使用产品价格）',
    variantPricePlaceholder: '价格',
    variantStock: '库存（留空 = 无限）',
    variantStockPlaceholder: '库存',
    sku: 'SKU',
    skuPlaceholder: '例：WHT-M-001',
    addVariant: '新增规格',
    noVariants: '暂无规格',
    noVariantsHint: '未设置规格。新增后客户下单时需要选择。',
    adding: '新增中...',
    list: '上架',
    unlist: '下架',
    typeLabel: '类型',
    priceHKD: '价格 (HKD)',
    priceInvalid: '价格必须为 0 或以上。',
    minutes: '分钟',
    createdAt: '创建于',
    totalSales: '总销量',
    totalRevenue: '总收入',
    orderCount: '订单数',
    noOrderHistory: '暂无订单记录',
    listNow: '立即上架',
    followsProduct: '跟产品',
    stock: '库存',
    notFound: '找不到产品',
    serviceDurationLabel: '服务时长',
    concurrentLabel: '同时服务人数',
    images: '产品图片',
    uploadImages: '上传图片',
    noImages: '暂无产品图片',
    noImagesHint: '上传产品图片，AI 推荐产品时会自动发送给客人。',
    setPrimary: '设为封面',
    primary: '封面',
    uploading: '上传中…',
    dragOrClick: '拖放或点击上传',
    imageFormats: '支持 JPG、PNG、WebP',
    resourceRequirements: {
      label: '所需资源',
      hint: '设置后，系统会在预约时自动分配空闲资源，资源不足的时段会自动隐藏。',
      itemLabel: '{quantity}× {label}',
      emptyHint: '未设置需求 — 预约不会自动预留房间或设备。',
      noCategories: '请先建立至少一个资源类别，才能添加需求。',
      manageCategories: '管理类别',
      addRequirement: '添加需求',
      modeCategory: '按类别',
      modeSpecific: '指定资源',
      modeHintCategory: '类别中任何一个空闲资源都可以。',
      modeHintSpecific: '指定具体哪几台／哪几间可用（例如「A 机或 B 机」）。',
      selectCategory: '选择类别',
      selectResources: '选择资源',
      quantity: '数量',
      specificSummary: '指定 {count} 个资源',
      save: '保存',
      cancel: '取消',
      errAdd: '加入失败',
      errRemove: '移除失败',
      errTargetRequired: '请选择类别、指定资源、或两者并用。',
    },
    pasteExtract: {
      buttonLabel: '粘贴文字自动识别',
      buttonTooltip: '粘贴菜单或服务清单，AI 自动提取产品资料',
      title: '粘贴 & 提取',
      descriptionPaste: '粘贴你的菜单、服务清单或价目表。AI 会提取成一行行，你审核后再保存。',
      descriptionReview: '检查 {count} 项提取的产品。可以修改或删除不对的项目。',
      descriptionDone: '完成！',
      placeholders: {
        salon:
          '什么都可以粘贴 — 服务清单、价目表。例：\n\n凝胶指甲 — $280 / 90 分钟\n修甲 — $120 / 45 分钟\n美甲彩绘加收 — $50',
        restaurant:
          '什么都可以粘贴 — 菜单、价目表。例：\n\n招牌炒饭 — $68\n馄饨面 — $48\n冰柠檬茶 — $28',
        cafe:
          '什么都可以粘贴 — 饮品菜单、轻食清单。例：\n\n拿铁咖啡 — $38\n冰滴咖啡 — $32\n可颂 — $28\n芝士蛋糕 — $45',
        retail:
          '什么都可以粘贴 — 商品清单、价目表。例：\n\n纯棉 T 恤 (S/M/L) — $180\n牛仔夹克 — $680\n帆布袋 — $120',
        medical:
          '什么都可以粘贴 — 服务清单、诊疗费。例：\n\n初诊 — $600 / 45 分钟\n复诊 — $400 / 30 分钟\n针灸 — $500',
        fitness:
          '什么都可以粘贴 — 课程清单、套餐价格。例：\n\n单堂课 — $180 / 60 分钟\n10 堂套餐 — $1,500\n1 对 1 私教 — $800 / 60 分钟',
        education:
          '什么都可以粘贴 — 课程清单、套餐价格。例：\n\n一对一教学 — $500 / 60 分钟\n4 堂套餐 — $1,800\n团体班 — $200 / 90 分钟',
        professional:
          '什么都可以粘贴 — 服务清单、收费表。例：\n\n首次咨询 — $1,200 / 60 分钟\n一般 engagement — $5,000 起\n月费 retainer — $8,000',
        generic:
          '什么都可以粘贴 — 产品或服务清单、价目表。\n每行格式：名称 — 价格 — 备注（可选）\n\n例：\n产品 A — $100\n服务 B — $200 / 30 分钟',
      },
      extract: '提取',
      extracting: '正在读取你的 catalog⋯',
      currencyDetected: '识别到货币：{currency}',
      colName: '名称',
      colPrice: '价格',
      colDescription: '描述',
      colDuration: '时长（分钟）',
      typeProduct: '产品',
      typeService: '服务',
      addRow: '添加一行',
      removeRow: '删除此行',
      startOver: '重新开始',
      publishNow: '导入后立即上架 — AI 可以马上报价和接受预约。取消勾选则保存为草稿。',
      doneDrafts: '{count} 项已保存为草稿，上架前 AI 不会报价。',
      importButton: '导入 {count} 项',
      importing: '导入中⋯',
      doneSummary: '成功导入 {count} 项。',
      doneFailed: '{count} 项无法导入 — 可能超过方案上限。',
      cancel: '取消',
      close: '关闭',
      errorTooShort: '粘贴多几项看看，AI 要有内容才能提取。',
      errorExtractFailed: '这段文字无法提取。试试减少项目或清晰一点的格式。',
      errorNoItems: '找不到产品。试试清晰一点的格式。',
      errorNetwork: '网络错误 — 请再试一次。',
    },
  },

  orders: {
    title: '订单',
    noOrders: '暂无订单',
    noOrdersHint: '客户下单后会显示在这里',
    orderNumber: '订单号',
    customer: '客户',
    amount: '金额',
    status: '状态',
    time: '时间',
    actions: '操作',
    markPaid: '标记已付款',
    markPaidConfirm: '确定将此订单标记为已付款？',
    confirmOrder: '确认订单',
    confirmOrderConfirm: '确定确认此订单？客户会收到通知。',
    completeOrder: '完成订单',
    completeOrderConfirm: '确定将此订单标记为完成？客户会收到通知。',
    markShipped: '标记已发货',
    markShippedConfirm: '确定将此订单标记为已发货？',
    markDelivered: '标记已送达',
    markDeliveredConfirm: '确定将此订单标记为已送达？',
    cancelOrder: '取消订单',
    cancelOrderConfirm: '确定取消此订单？此操作无法撤销，库存会自动恢复。',
    cancelReason: '取消原因（可留空）：',
    pendingConfirm: '待确认',
  },

  customers: {
    title: '客户',
    searchPlaceholder: '搜索名称、电话...',
    allChannels: '全部渠道',
    sortLastSeen: '最近互动',
    sortEngagement: '活跃度',
    sortSpent: '总消费',
    sortOrders: '订单数',
    sortName: '名称',
    noCustomers: '暂无客户记录',
    noCustomersHint: '尝试调整搜索或筛选条件',
    noCustomersEmpty: '客户发送消息后会显示在这里',
    customerOverview: '客户概览',
    count: '位客户',
  },

  bookings: {
    title: '预约',
    listTab: '预约列表',
    calendarTab: '日历',
    availabilityTab: '可预约时间',
    noBookings: '暂无预约',
    noBookingsHint: '客户通过 WhatsApp 预约后会显示在这里',
    selectHint: '选择一个预约查看详情',
    titleFnb: '订位',
    listTabFnb: '订位列表',
    availabilityTabFnb: '可订时段',
    noBookingsFnb: '暂无订位',
    noBookingsHintFnb: '客户通过 WhatsApp 订位后会显示在这里',
    selectHintFnb: '选择一个订位查看详情',
    addBookingFnb: '新增订位',
    today: '今天',
    allDay: '全天',
    upcoming: '即将到来',
    service: '服务',
    duration: '时长',
    durationMin: '分钟',
    customer: '客户',
    phone: '电话',
    date: '日期',
    time: '时间',
    price: '价格',
    notes: '备注',
    editNotes: '编辑备注',
    noNotes: '无备注',
    records: '记录',
    recordsHint: '为每次预约留下上下文 — 之后跟进、同事接手都更轻松',
    addRecord: '添加记录',
    recordPlaceholder: '今日进度 / 客户反馈 / 疗程设定 / 下次跟进⋯',
    recordEmpty: '暂无记录',
    saveRecord: '保存',
    cancelRecord: '取消',
    editRecord: '编辑',
    deleteRecord: '删除',
    deleteRecordConfirm: '删除这条记录？已上传的附件将一并删除。',
    attachFile: '加附件',
    attaching: '上传中⋯',
    attachmentTooLarge: '文件超过 5MB',
    attachmentRejected: '不支持此文件类型（仅接受图片与 PDF）',
    attachmentLimit: '每条记录最多 5 个附件',
    sessionCounter: '第 {n} 次（共 {m} 次）',
    sessionRemaining: '剩 {n} 次',
    deposit: '订金',
    depositRequired: '需订金 {pct}%',
    depositPaid: '已收订金',
    depositPending: '订金未收',
    cancelReason: '取消原因',
    viewOrder: '查看订单',
    viewCustomer: '查看客户',
    viewConversation: '查看对话',
    createdAt: '创建时间',
    updatedAt: '最后更新',
    noShow: '未到',
    confirmBooking: '确认',
    completeBooking: '完成',
    cancelBooking: '取消',
    payment: '付款',
    search: '搜索客户名称或电话⋯',
    staffTab: '服务人员',
    resourcesTab: '场地设备',
    staffTitle: '服务人员管理',
    addStaff: '添加人员',
    editStaff: '编辑人员',
    staffName: '显示名称',
    staffNameZh: '中文名称',
    staffTitleField: '显示称呼（选填）',
    staffTitlePlaceholder: '例：Dr. 梁医生 / 治疗师 Stella / Felix sir / 技师 Dave',
    staffTitleLegalHint: '「医生」、「医师」、「物理治疗师」、「注册护士」属法律保护称号 — 请确保具备资格使用。',
    staffFallbackLabel: '负责同事',
    staffColor: '颜色',
    staffServices: '可提供服务',
    staffSchedule: '排班表',
    staffBlockedDates: '休假日',
    noStaff: '暂无服务人员',
    noStaffHint: '添加服务人员以启用指定人员预约',
    staffActive: '在职',
    staffInactive: '已停用',
    deactivateStaff: '停用',
    activateStaff: '启用',
    unassigned: '未指派',
    autoAssign: '自动分配',
    assignedTo: '指派给',
    assignedResources: '已安排设备',
    reassign: '重新指派',
    enableStaffMode: '启用指定服务人员',
    disableStaffMode: '关闭指定服务人员',
    staffModeHint: '启用后客户预约时可以选择指定服务人员',
    resources: {
      title: '场地 / 设备',
      description: '房间、椅子、仪器等资源 — 系统会防止同一时段重复预约。',
      addResource: '新增资源',
      allLocations: '所有门店',
      emptyTitle: '暂无资源',
      emptyDesc: '添加房间、椅子或仪器以追踪使用情况。需要特定房间的服务会在预约时自动分配。',
      gatedTitle: '场地 / 设备是商业版功能',
      gatedDesc: '追踪房间、椅子与仪器，在数据库层级防止重复预约。适合有共用工位的美容院、有诊室的诊所，或需要专用仪器的工作室。',
      gatedBullet1: '数据库层级的重复预约防护',
      gatedBullet2: '按门店追踪房间与设备',
      gatedBullet3: '容量上限与清理缓冲时间',
      gatedBullet4: '预约时自动分配资源',
      pitchMedical: '医美项目一撞机就失信于客户 — HIFU、光子嫩肤、皮秒激光只要撞到就很难补救。场地设备功能在数据库层面保证两位客人不会同时占用同一台机。',
      pitchSalon: '一间疗程房、两位同事、两位客人 — 撞预约只是时间问题。场地设备追踪每间房、每张椅、每台机，AI 助理不会给出做不到的时段。',
      pitchRestaurant: '卡位、包厢、吧台 — 用场地设备对应到预约，AI 只会 confirm 你实际能服务的时段。',
      pitchGeneric: '保护房间、椅子、设备不被重复预约。AI 助理只会让客人选择系统真的能提供的时段。',
      inlineUpsellTitle: '这个服务可能需要指定房间或机器？',
      inlineUpsellBody: '升级 Business 版可以将服务绑定指定房间、椅子或仪器 — 预约时会自动隐藏无法分配的时段。',
      planBadge: 'Business 方案',
      previewLabel: '示例类别',
      viewPlans: '查看方案',
      needLocation: '至少需要一个启用中的门店才能新增资源。',
      privateLabel: '私人',
      cleanupLabel: '清理：{n} 分钟',
      edit: '编辑',
      editTitle: '编辑资源',
      addTitle: '新增资源',
      name: '名称',
      namePlaceholder: '例：1 号房、A 椅、HIFU 机…',
      category: '类别',
      categoryHint: '从你自建的类别中选择。管理类别请至"类别"设置。',
      manageCategories: '管理类别',
      noCategoriesYet: '暂无类别',
      createCategoryFirst: '请先建立资源类别，才能新增资源。',
      location: '门店',
      selectPlaceholder: '— 请选择 —',
      capacity: '容量',
      capacityHint: '可同时接待的人数。一般房间填 1，团体课可填更多。',
      cleanup: '清理时间（分钟）',
      notes: '备注',
      notesPlaceholder: '内部备注、维护计划等',
      privateCheckbox: '私人 — 客户指定要私人房时优先分配',
      errName: '请填写名称',
      errLocation: '请选择门店',
      errCategory: '请选择类别',
      errSaveFailed: '保存失败',
      errModuleOff: '进阶预约功能未开启，请先到「设置 → 模块」开启',
      errPlanLocked: '你的套餐未包含房间及设备',
      errCategoryArchived: '此类别已归档，请先恢复或改选其他类别',
      errNotFound: '门店、类别或资源已不存在，请重新加载页面',
      errDeactivateFailed: '停用失败',
      deactivateConfirm: '停用"{name}"？',
      cleanupHint: '每次预约后的清洁 / 准备时间，服务结束后资源会继续被占用这段时间。',
      inUseConfirm: '停用后，此分店将无法预约：{services}。确定停用？',
      futureBookingsNote: '仍有 {n} 个未来预约在使用此资源，请检查：{list}',
      outOfService: {
        title: '暂停使用',
        hint: '维修中或暂停一段时间？标记时段后，AI、在线预约页和后台在该时段都不会再提供。设定的时间范围适用于每一天。',
        button: '暂停使用',
        none: '暂无已安排的暂停时段。',
        upcoming: '{n} 个即将',
        add: '新增',
        from: '从',
        to: '至',
        startTime: '开始时间',
        endTime: '结束时间',
        timeHint: '两个时间都留空表示全天。',
        type: '类型',
        typeMaintenance: '维修保养',
        typeUnavailable: '暂停',
        reason: '原因',
        reasonPlaceholder: '例如：原厂例行保养 + 校准',
        allDay: '全天',
        remove: '移除',
        removeConfirm: '恢复「{name}」{when} 的使用？',
        bookedTitle: '该时段有 {n} 个未来预约在使用此资源',
        bookedHint: '这些预约会保留时段，不会被取消。仍然保存后，请改期或联系客人。',
        saveAnyway: '仍然保存',
        savedWithBookings: '已保存。该时段仍有 {n} 个预约在使用此资源，请改期或联系客人。',
        errDates: '请检查日期：结束日不能早于开始日，也不能是过去。',
        errTimes: '请填写两个时间，结束须晚于开始。',
        errSpan: '最长一年；更长时间请直接停用资源。',
        errFailed: '保存失败',
      },
      cancel: '取消',
      save: '保存',
      saving: '保存中…',
    },
    resourceCategories: {
      title: '资源类别',
      resourceCount: '{count} 个资源',
      description: '将资源（治疗房、特定仪器、座位等）分组为类别，服务可以引用这些类别决定要预约什么。',
      addCategory: '新增类别',
      emptyTitle: '暂无类别',
      emptyDesc: '类别是你用来描述可预约资源的分类 — 例如"治疗房""HIFU 机""洗发椅"。加入符合你业务的类别。',
      editTitle: '编辑类别',
      addTitle: '新增类别',
      name: '名称',
      namePlaceholder: '例：治疗房、HIFU 机…',
      nameZh: '名称（中文）',
      nameZhPlaceholder: '可选的本地化名称',
      categoryDescription: '描述',
      descriptionPlaceholder: '这个类别用来做什么？',
      icon: '图标',
      color: '颜色',
      sortOrder: '排序',
      save: '保存',
      cancel: '取消',
      saving: '保存中…',
      delete: '封存',
      deleteConfirm: '封存类别"{name}"？',
      deleteInUse: '无法封存：仍有 {count} 个启用中资源使用此类别，请先处理。',
      errName: '请填写名称',
      errDuplicate: '已有同名的类别',
      errSaveFailed: '保存失败',
      errDeleteFailed: '封存失败',
    },
  },

  inquiries: {
    title: '客户咨询',
    noInquiries: '暂无咨询',
    noInquiriesHint: 'AI 记录的客户咨询会显示在这里',
    backToList: '返回列表',
    customerLabel: '客户：',
    phoneLabel: '电话：',
    createdLabel: '创建时间：',
    resolvedLabel: '解决时间：',
    viewConversation: '查看相关对话 →',
    descriptionLabel: '描述',
    resolutionNote: '解决备注',
    resolutionNotePlaceholder: '解决备注...',
    markInProgress: '标记处理中',
    markResolved: '标记已解决',
    updating: '更新中...',
    reopen: '重新开启',
    selectInquiry: '选择一个咨询查看详情',
  },

  knowledge: {
    title: '知识库',
    searchPlaceholder: '搜索知识库...',
    noKnowledge: '暂无知识',
    noKnowledgeSearch: '未搜索到相关内容',
    noKnowledgeHint: '新增常见问题、政策、门店信息等，AI 会自动用来回答客户。',
    categories: { faq: '常见问题', policy: '政策', info: '信息', general: '通用' },
    aiKeywords: 'AI 已学习',
    aiGeneratingKeywords: 'AI 学习中...',
    aiTraining: '学习中',
    aiReady: '已就绪',
    editKnowledge: '编辑知识',
    addKnowledge: '新增知识',
    editHint: '修改知识内容，AI 会自动更新搜索关键词。',
    addHint: '新增知识后，AI 会自动生成搜索关键词，方便之后搜索。',
    titleField: '标题',
    titlePlaceholder: '例：退货政策',
    contentField: '内容',
    contentPlaceholder: 'AI 会根据这段内容回答客户问题',
    categoryField: '分类',
    updatedAt: '更新时间：',
    selectKnowledge: '选择一条知识查看详情',
    import: '导入',
    importTitle: '导入知识',
    importSourceUrl: '网址',
    importSourceFile: '文件',
    importSourceText: '粘贴文字',
    importUrlPlaceholder: '输入网页网址，例如 https://myshop.com/faq',
    importTextPlaceholder: '直接粘贴文字内容（FAQ、政策、服务说明等）',
    importFileDrop: '拖放文件或点击选择',
    importFileSupported: '支持 PDF、Word、PowerPoint、TXT、图片',
    importExtracting: '正在提取内容...',
    importExtractingHint: 'AI 正在读取并整理您的内容',
    importPreviewTitle: '预览提取内容',
    importPreviewEdit: '如内容有误，可直接编辑',
    importSplitting: '正在拆分知识...',
    importSplittingHint: 'AI 正在将内容拆分成独立知识条目',
    importReviewTitle: '确认知识条目',
    importReviewCount: 'AI 已整理出 {count} 条知识',
    importSelectAll: '全选',
    importAsDraft: '导入为草稿',
    importPublish: '直接发布',
    importPublishFailed: '有 {n} 条未能保存，已保留勾选，请再试一次。',
    importLikelyDuplicate: '可能与现有的「{title}」重复 — 已取消勾选，确认不同才发布。',
    importBack: '返回',
    importNext: '继续',
    importStart: '开始提取',
    importError: '导入失败',
    importNoContent: '未能提取到有效内容',
    tabEntries: '知识条目',
    tabSources: '来源管理',
    sourcesEmpty: '暂无导入来源',
    sourcesEmptyHint: '导入文件或网页，AI 会自动整理成知识库',
    sourceEntries: '{count} 条知识',
    sourceProcessing: '处理中...',
    sourceFailed: '处理失败',
    sourceDeleteTitle: '删除来源',
    sourceDeleteKeep: '仅删除来源',
    sourceDeleteAll: '连同知识一起删除',
    sourceDeleteKeepHint: '保留已生成的知识条目',
    sourceDeleteAllHint: '此来源生成的 {count} 条知识也会被删除',
    filterBySource: '来源',
    sourceFilterAll: '全部来源',
    sourceFilterManual: '手动新增',
    sourceFilterConversation: '对话提取',
    sourceBadgeManual: '手动',
    sourceBadgeFile: '文件',
    sourceBadgeUrl: '网页',
    sourceBadgeConversation: '对话',
    sourceBadgeImage: '图片',
    sourceBadgeText: '文字',
    entryFromSource: '来源',
    viewSource: '查看来源',
  },


  usage: {
    title: '用量',
    loadFailed: '加载失败',
    planNames: { free: '免费', starter: '入门', pro: '专业', business: '商业', enterprise: '企业' },
  },

  plan: {
    limitReachedTitle: '套餐未包含此功能',
    scaleCapTitle: '已达上限',
    limitReachedBody: '你的 {plan} 套餐未包含 {feature}，升级后即可使用。',
    scaleCapBody: '你的 {plan} 套餐的 {feature} 已达上限，升级到 {target} 解锁更多。',
    upgradeCta: '升级到 {plan}',
    manageOnWeb: '请在浏览器打开 app.novixone.ai 管理你的套餐',
    quotaReachedBody: '本月的 {feature} 额度已用完。',
    quotaUsage: '本月已使用 {used} / {limit}',
    unlimited: '无限制',
    creditsExhaustedTitle: 'AI credits 已用完',
    creditsExhaustedBody:
      '本期的 AI credits 已用完，AI 功能暂时停用。购买 credit pack 或升级套餐即可继续使用。',
    creditsExhaustedCta: '前往账单设置',
    featureNames: {
      monthlyReplies: 'AI 回复',
      monthlyKnowledgePagesImported: '知识库导入页数',
      maxTeamMembers: '团队成员',
      maxWhatsappNumbers: 'WhatsApp 号码',
      maxProducts: '商品',
      maxKnowledgeEntries: '知识库条目',
      maxCustomerSegments: '客户分群',
      maxAutomationRules: '自动化规则',
      customRoles: '自定义角色',
      knowledgeAdvancedImport: '高级知识导入 (PDF / URL / OCR)',
      cohortFunnelAnalytics: '群组 / 漏斗分析',
      conversationalAnalytics: 'AI 问数（对话式分析）',
      integrationEvents: '事件推送（Webhook）',
      publicApiRead: 'API 读取',
      publicApiWrite: 'API 写入',
      aiActions: 'AI 动作',
      integrationConnectors: '现成连接',
      providerContract: '连接你自己的系统',
      customDomain: '自定义域名',
    },
  },

  login: {
    heroTitle1: '将客户对话',
    heroTitle2: '转化为',
    heroTitle3: '营收',
    heroSubtitle: 'AI 在 WhatsApp、LINE 及网站对话中 24/7 为您接待客户、下单、跟进。',
    features: {
      aiSupport: '全渠道 AI 客服',
      aiSupportDesc: '24/7 多语言 AI 自动回复，帮您接单、回答咨询、预约',
      orderViaChat: '对话即下单',
      orderViaChatDesc: '客户在 WhatsApp、LINE 对话中直接下单，无需跳转',
      memory: '客户记忆',
      memoryDesc: 'AI 记住每位客户的偏好，提供个性化服务',
    },
    orEmail: '或用邮箱登录',
    emailLabel: '邮箱地址',
    emailPlaceholder: '',
    checkEmail: '检查您的邮箱',
    emailSentTo: '已发送登录链接到',
    clickLink: '请点击邮件中的链接登录后台',
    backToLogin: '← 返回登录',
    signInTitle: '登录后台',
    signInSubtitle: '登录您的 NovixOne 账号',
    signUpTitle: '创建账号',
    signUpSubtitle: '创建 NovixOne 账号，5 分钟完成设置',
    googleSignIn: '使用 Google 账号登录',
    googleSignUp: '使用 Google 账号注册',
    orEmailSignUp: '或使用邮箱注册',
    freePlanBadge: '免费方案：每月 100 位活跃客户 · 无需信用卡',
    joinTeamTitle: '加入团队',
    joinTeamSubtitle: '店铺已邀请您使用 NovixOne。创建账号后，即可接受邀请并开始处理对话、预约及客户。',
    joinTeamCardSubtitle: '请使用收到邀请的邮箱地址，完成后会返回邀请页。',
    bookDemo: '预约演示',
    worksWith: '支持渠道',
    webChat: '网站对话',
    trustLine: '数据加密 · 隐私设计 · 数据由您掌控',
    passwordHint: '至少 8 个字符',
    recommendedBadge: '推荐',
    welcomeBack: '欢迎回来',
    verifyEmailTitle: '验证您的邮箱',
    verifyEmailSentTo: '我们已发送验证链接至',
    verifyEmailHint: '请点击邮件中的链接确认身份，之后即可继续设置 NovixOne。',
    sending: '发送中...',
    sendLink: '发送登录链接',
    passwordLabel: '密码',
    passwordPlaceholder: '输入您的密码',
    signIn: '登录',
    signUp: '创建账号',
    signingIn: '登录中...',
    signingUp: '创建账号中...',
    noAccount: '还没有账号？',
    hasAccount: '已有账号？',
    forgotPassword: '忘记密码？',
    switchToSignUp: '注册',
    switchToSignIn: '登录',
    orMagicLink: '或使用魔法链接登录',
    orPassword: '或使用邮箱和密码登录',
    forgotPasswordTitle: '重置密码',
    forgotPasswordSubtitle: '输入您的邮箱，我们将发送重置链接',
    sendResetLink: '发送重置链接',
    sendingReset: '发送中...',
    resetEmailSent: '重置链接已发送',
    resetEmailSentDesc: '请检查邮箱中的密码重置链接',
    backToSignIn: '返回登录',
    resetPasswordTitle: '设置新密码',
    resetPasswordSubtitle: '请输入您的新密码',
    newPasswordLabel: '新密码',
    newPasswordPlaceholder: '至少 6 个字符',
    confirmPasswordLabel: '确认密码',
    confirmPasswordPlaceholder: '再次输入密码',
    updatePassword: '更新密码',
    updatingPassword: '更新中...',
    passwordMismatch: '两次输入的密码不一致',
    passwordTooShort: '密码至少需要 6 个字符',
    passwordResetSuccess: '密码已更新成功',
    passwordResetRedirect: '正在跳转到后台...',
    termsAgreement: '创建账户即代表您同意我们的',
    legalLinks: { terms: '服务条款', privacy: '隐私政策', aup: '可接受使用政策' },
    trustEncryption: '数据加密',
    trustSecurity: '安全保障',
    trustSetup: '快速设置',
    trustSetupTime: '5 分钟',
    trustAutoReply: 'AI 自动回复',
    trustAutoReplyTime: '24/7',
    footer: '© 2026 Novix Star Limited · novixone.ai',
  },

  onboarding: {
    title: 'NovixOne 设置',
    subtitle: '一分钟内设置你的 AI 助手',
    setupComplete: '设置完成！',
    redirecting: '正在跳转至控制台...',
    redirectHint: '按照设置清单完成其余设置',
    settingUp: '正在设置中...',
    step1Title: '商家资料',
    step1Subtitle: '介绍你的业务',
    step2Title: 'AI 助手',
    step2Subtitle: '自定义你的 AI',
    step3Title: '确认并启动',
    step3Subtitle: '检查并开始',
    businessNameLabel: '商家名称',
    businessNamePlaceholder: '例如：明轩家品',
    industryLabel: '行业',
    // 会员计划步骤（在 AI 助手之后、确认之前）
    regionLabel: '主要营业地区',
    aiNameLabel: 'AI 助手名称',
    aiNamePlaceholder: '留空将使用你的商家名称',
    toneLabel: 'AI 对话风格',
    toneFriendly: '友好',
    toneProfessional: '专业',
    toneCasual: '随意亲切',
    summaryTitle: '设置摘要',
    editButton: '修改',
    next: '下一步',
    back: '上一步',
    launch: '启动',
    // ── V3 对话式 onboarding ──
    describeTitle: '说说你的生意',
    describeSubtitle: '一两句话就够 — 做什么行业、在哪里、有什么特色。',
    describeHint: '没想法？点一下这些：',
    describeContinue: '继续',
    describeAnalyzing: '正在理解你的生意⋯',
    exampleChipFnb: '餐饮 / 饮品',
    exampleChipSalon: '美容 / 美甲',
    exampleChipRetail: '零售',
    exampleChipProfessional: '专业服务',
    exampleChipHealth: '健康 / 保健',
    exampleChipEducation: '教育',
    exampleChipOther: '其他',
    reviewTitle: '确认资料',
    reviewSubtitle: '看看我理解得对不对。绿色表示有信心；琥珀色请再核对一下。',
    reviewOriginalLabel: '你说了：',
    reviewReDescribe: '← 重新描述',
    aiInferredBadge: 'AI 分析',
    verifyBadge: '请核对',
    subTypeLabel: '细项',
    subTypePlaceholder: '例如：凝胶指甲 + 零售',
    clarificationHint: '多问一下：',
    flaggedTitle: '需要我们看一下',
    flaggedBody: '你描述中有些地方需要人工审核才能开通账户，我们的同事会尽快联系你。',
    extractionFailedHint: '无法自动分析你的描述，请手动填写下方栏位。',
  },

  activate: {
    heroTitle: '设置你的 AI 助理',
    heroSubtitle: '账户已建立。跟着下面步骤设置完，AI 就能很好地接待客人，然后连接 WhatsApp 即可正式上线。',
    progressLabel: '完成 {done} / {total} 步',
    recommendedBadge: '建议',
    doneBadge: '完成',
    catalogTitleCommerce: '添加你的产品',
    catalogTitleBooking: '添加你的服务',
    catalogDescription: '粘贴菜单或服务清单。AI 需要这些资料才能报价、下单。',
    catalogDone: '已添加 {count} 项',
    hoursTitle: '设置预约时段',
    hoursDescription: '什么时间开放客人预约？AI 用这个检查可用时段。',
    hoursDone: '已设置 {count} 个时段',
    policiesTitle: '设置政策',
    policiesDescription: '取消期限、退换规则、订金。没有固定规则可留空 — AI 会直接转给你处理。',
    paymentTitle: '设置收款',
    paymentDescription: '连接 Stripe，或列出当地付款方式。',
    paymentDone: '收款已设置',
    teachTitle: '一步教会你的 AI',
    teachSubtitle: '粘贴你的网站，或常见问题／价目表。我们会整理成 AI 用得上的答案，你可以在知识库检查。',
    teachUrlTab: '网站链接',
    teachTextTab: '粘贴文字',
    teachUrlPlaceholder: 'https://your-shop.com',
    teachTextPlaceholder: '营业时间、价格、地址、如何预约、付款方式、取消政策⋯',
    teachSubmit: '教会我的 AI',
    teachSubmitting: '读取中⋯',
    teachStarted: '收到！AI 正在读取，一两分钟内就会在知识库看到整理好的答案。',
    teachFailed: '无法读取，试试直接粘贴文字。',
    teachViewKnowledge: '打开知识库',
    knowledgeTitle: '教 AI 了解你的生意',
    knowledgeDescription: 'FAQ 与店家专属资料。可以粘贴现有素材，或上线后让 AI 从你的实际回复中累积。',
    knowledgeDone: '已有 {count} 条知识',
    sandboxTitle: '在测试模式试试',
    sandboxDescription: '扮演客人与 AI 对话 — 至少 3 条消息才够深入，找出真正的问题。',
    sandboxProgress: '已测 {count}/{total} 条 — 继续试一下。',
    sandboxDone: '已测试 — 看看有没有需要调整？',
    goLiveTitle: '准备上线了？',
    goLiveDescription: '连接 WhatsApp 就能接待真实客人。',
    goLiveWarning: '还有 {count} 个建议步骤未完成 — 仍可上线，但 AI 会更常转给你处理。',
    goLiveButton: '连接 WhatsApp',
    skipToDashboard: '暂时略过 — 先前往总览',
    noSellingModulesHint: '订单和预约都未开启，所以没有产品、时段和收款步骤。',
    manageFeaturesLink: '管理功能',
    liveTitle: '你已经上线了！',
    liveSubtitle: '你的 AI 现在正在 WhatsApp 接待真实客户。',
    liveTestCta: '发一条测试消息',
    liveDashboardCta: '前往总览',
    liveRemainingHint: '还有 {pending} 个步骤未完成 — 每完成一项，AI 转给你处理的对话就会少一些。',
  },

  getStarted: {
    title: '欢迎来到 NovixOne 👋',
    subtitle: '开始之前，请花一分钟确认以下条款。我们会在下一步帮您设置商户账户。',
    legalIntro: '本服务由 诺辰科技有限公司（Novix Star Limited）提供。请阅读并同意以下文件：',
    docs: { tos: '服务条款', privacy: '隐私政策', aup: '可接受使用政策', dpa: '数据处理协议' },
    checkbox1Prefix: '我已阅读并同意',
    checkbox1Sep: '、',
    checkbox1And: '、',
    checkbox1End: '与',
    authorityInline: '签署即代表您有权代表您的商户',
    marketingOptIn: '我想接收 NovixOne 的产品更新、优惠与教程内容',
    marketingOptInHint: '可随时在设置中取消订阅',
    optionalLabel: '选填',
    button: '同意并继续',
    ctaRemaining: '还需勾选 {count} 项',
    submitting: '处理中...',
    error: '出错了，请重试',
    progressLabel: '设置进度',
    progressStep1: '同意条款',
    progressStep2: '商户资料',
    progressStep3: 'AI 设置',
    progressStep4: '启动',
    supportHint: '有问题？',
    supportEmail: 'support@novixone.ai',
  },

  legalBanner: {
    title: '我们更新了法律条款',
    desc: '请查看并重新确认最新的服务条款、隐私政策、可接受使用政策与数据处理协议。',
    review: '查看更新',
    accept: '我同意',
    accepting: '处理中...',
    error: '出错，请重试',
  },

  invite: {
    invalidLink: '邀请链接无效',
    teamInvite: '团队邀请',
    joinTeam: '加入团队',
    inviteMessage: '您已被邀请加入 NovixOne 团队。',
    clickToJoin: '点击下方按钮确认加入。',
    accept: '接受邀请',
    joining: '加入中...',
    successJoined: '成功加入{name}团队！',
    redirecting: '正在跳转到后台...',
    backToLogin: '返回登录',
    invitedBy: '{inviter} 邀请您加入',
    forEmail: '邀请对象：{email}',
    createAccount: '创建账号并加入',
    signIn: '已有账号？登录',
    wrongAccount: '您当前以 {current} 登录，但此邀请是发给 {invitee} 的。请以该邮箱登录后再接受。',
    switchAccount: '退出并更换账号',
    errorInvalid: '此邀请已失效或已被撤回，请向店铺索取新的邀请。',
    errorMismatch: '此邀请是发给另一个邮箱地址的，请以受邀邮箱登录。',
    errorGeneric: '未能加入团队，请稍后再试。',
  },

  pay: {
    successTitle: '付款成功',
    successMessage: '感谢您的购买！我们已经收到您的付款。',
    orderNumber: '订单编号：',
    successHint: '您可以关闭此页面，返回 WhatsApp 继续对话。',
    cancelTitle: '付款已取消',
    cancelMessage: '您的付款未完成。如需重新付款，请返回 WhatsApp 联系商户。',
    cancelHint: '您可以关闭此页面。',
  },

  setup: {
    title: '完成设置',
    progress: '{done}/{total} 项完成 · {pct}%',
    addServices: '添加服务项目',
    addProducts: '添加产品／服务',
    productsDone: '已添加 {count} 项',
    productsHint: 'AI 需要知道您卖什么才能推荐',
    paymentSetup: '设置付款方式',
    paymentStripe: 'Stripe 在线收款已连接',
    paymentLocal: '已设置手动付款方式',
    paymentHint: '连接 Stripe 或添加手动付款方式（转帐、移动钱包、现金等）',
    knowledgeFaq: '填写常见问题',
    knowledgeBase: '填写知识库',
    knowledgeDone: '已添加 {count} 条数据',
    knowledgeHintSupport: '常见问题、使用指南等，AI 回答更准确',
    knowledgeHint: '常见问题、退换政策等，AI 回答更准确',
    knowledgeDraftsPending: '有 {count} 条捕获的草稿等待你审核',
    bookingSetup: '设置预约时段',
    bookingDone: '已设置 {count} 个时段',
    bookingHint: '设置开放时间，客户才能通过 AI 预约',
    askCopilot: '使用 AI 助手帮忙设置',
    sandboxTest: '测试 AI 对话',
    sandboxDone: '已在沙盒测试 AI',
    sandboxHint: '上线前以客户身份试试您的 AI',
    segmentSetup: '设置客户分群',
    segmentHint: '按行为对客户分类，可针对性进行营销',
    segmentCount: (n) => `已建立 ${n} 个分群`,
    outreachSetup: '设置主动营销',
    outreachDone: '营销模板已就绪',
    outreachHint: '为弃单与流失客户设置自动跟进',
  },

  sandbox: {
    title: 'AI 沙盒',
    subtitle: '以客户身份测试您的 AI 助手。这里的数据与正式环境完全隔离。',
    inputPlaceholder: '以客户身份输入消息...',
    newConversation: '新对话',
    clearAll: '清除所有测试数据',
    clearConfirm: '将会删除所有沙盒测试数据，包括对话、订单、预约、咨询及客户记录，确定继续？',
    cleared: '所有测试数据已清除',
    creditsRemaining: '剩余 {n} Credits',
    creditsExhausted: 'AI Credit 余额已用完，充值后可继续测试。',
    noMessages: '发送消息开始测试您的 AI',
    banner: '沙盒模式 — 这里的对话与数据不会影响正式环境',
    history: '测试记录',
  },

  push: {
    title: '开启推送通知',
    description: '客户需要人工接手时即时通知你，不会错过任何对话。',
    allow: '允许通知',
    dismiss: '关闭',
    integrationApprovalTitle: '需要批准：{action}',
  },

  copilot: {
    title: 'AI 助手',
    greeting: '有什么可以帮您？',
    clearChat: '清除对话',
    welcomeTitle: 'AI 助手',
    welcomeHint: '您可以用自然语言管理您的业务，例如："添加服务『空调清洗』，$480"、"今天有多少预约？"，或者上传产品清单 / 价目表图片',
    uploadImage: '上传图片',
    pendingUpload: '待上传',
    analyzeImage: '请分析这张图片',
    imageInputPlaceholder: '描述图片或直接发送...',
    inputPlaceholder: '输入消息...',
    morningBriefing: '给我今日的 briefing',
    actionNotDoneYet: '尚未执行任何操作。',
    actionConfirm: '确认',
    actionCancel: '取消',
    actionChallengeMismatch: '输入不相符，请核对数字后再试。',
    actionNotAllowed: '您已没有此权限，或相关功能已关闭。',
    actionError: '未能完成，请再试一次。',
    uploadFailed: '上传失败。图片须小于 4 MB。',
    workingSteps: '已完成 {n} 步',
    continueRun: '继续',
    batchApprove: '批准所选的 {n} 项',
    batchCancelAll: '全部取消',
    batchTitle: '{n} 项待确认',
    batchFinished: '已完成 {ok} 项，{failed} 项未成功。',
    batchDeclined: '已全部取消，没有执行任何操作。',
    creditsUsed: '已用 {n} credits',
    stop: '停止',
    actionTainted: 'AI 是在读取他人撰写的内容（例如客户消息或文件）后提出此操作，请确认这是您想要的。',
    fileRejected: '无法使用此文件。支持 25 MB 以内的 Excel 或 CSV。',
    importThisFile: '请导入这个文件中的产品。',
  },

  settings: {
    title: '设置',
    locations: {
      title: '门店',
      description: '管理你的门店 / 分店，每家门店有自己的营业时间、员工、时区。',
      defaultBadge: '默认',
      additionalBranches: '其他门店',
      multiBranchLockedTitle: '多门店需要 Pro 或以上计划',
      multiBranchLockedBody: 'Pro 计划最多可管理 3 家门店，Business 计划最多 10 家，包括每家门店的营业时间、员工排班及设备。',
      branchLimitReachedTitle: '已达门店上限',
      branchLimitReachedBody: '升级到 Enterprise 解锁无限门店，或者先删一家。',
      viewPlans: '查看方案',
      addBranch: '新增门店',
      services: '服务',
      edit: '编辑',
    },
    channels: {
      title: '渠道',
      description:
        '已连接的消息渠道 — WhatsApp 号码及 LINE 帐号。管理每个渠道的名称、AI 员工及默认发送号码。',
      emptyState: '尚未连接任何渠道。',
      emptyStateHint: '连接 WhatsApp 号码或 LINE 官方帐号，让 AI 开始回复你的客户。',
      connectChannel: '连接渠道',
      connectWhatsApp: '连接 WhatsApp',
      connectLine: '连接 LINE',
      chooseChannelTitle: '选择要连接的渠道',
      whatsappOptionHint: '以 Meta 登录连接 WhatsApp Business 号码',
      lineOptionHint: '需要 LINE 官方账号（免费），约 10 分钟，附逐步教学',
      activeBadge: '已连接',
      sandboxBadge: '沙盒',
      defaultBadge: '默认',
      defaultHint: '群发及其他非对话内的消息会由默认渠道发出。',
      setDefault: '设为默认',
      connectedOn: '连接于 {date}',
      editLabel: '编辑名称',
      labelPlaceholder: '例如：主店、预约专线',
      agentLabel: 'AI 员工',
      agentInherit: '商户默认',
      disconnect: '解除连接',
      disconnecting: '解除中⋯',
      disconnectTitle: '解除连接 {name}？',
      disconnectBody: 'AI 将立即停止在此渠道回复。对话记录会保留，你可以随时重新连接。',
      disconnectDefaultNote: '此渠道是你的默认渠道 — 解除后，最早连接的其余渠道会自动成为新默认。',
      loadError: '无法加载渠道。',
      waTitle: '连接 WhatsApp Business',
      waIntro: '你将以 Facebook 登录，并在弹出窗口中选择（或创建）WhatsApp Business 号码。',
      waPrereq: '请准备：Meta Business 帐号，以及一个未注册到其他 WhatsApp 帐号的电话号码。',
      waContinue: '以 Facebook 继续',
      waWaiting: '等待 Facebook⋯',
      waWaitingHint: '请在 Facebook 窗口完成步骤，完成后会自动关闭。',
      waCancelled: 'Facebook 授权已取消。',
      waSuccessTitle: '号码已连接',
      waSuccessBody: '{number} 已连接，AI 会立即开始回复此号码的客户。',
      waConnectAnother: '连接另一个号码',
      error: '保存失败，请重试。',
    },
    templates: {
      title: 'WhatsApp 消息模板',
      description:
        '用于群发、自动跟进及系统通知（预约确认、提醒）的消息模板。模板须经 Meta 审核通过后才能发送。',
      manageLink: '管理 WhatsApp 消息模板',
      sync: '同步状态',
      syncing: '同步中…',
      syncDone: '已从 Meta 同步 {n} 个模板',
      syncFailed: '同步失败，请重试。',
      loadError: '无法加载模板。',
      emptyNotConnected: '请先连接 WhatsApp 号码',
      emptyNotConnectedCta: '前往频道设置',
      emptyNoTemplates: '尚未有任何模板。',
      emptyNoTemplatesHint: '点击「同步状态」向 Meta 注册标准模板并获取审核状态。',
      systemGroup: '系统模板',
      customGroup: '自定义模板',
      statusApproved: '已通过',
      statusPending: '审核中',
      statusPendingHint: '通常几分钟至 24 小时',
      statusRejected: '被拒绝',
      statusPaused: '已暂停',
      statusPausedHint: '因质量评分过低被 Meta 暂停，会自动恢复',
      statusDisabled: '已停用',
      qualityLabel: '质量',
      qualityGreen: '良好',
      qualityYellow: '一般',
      qualityRed: '欠佳',
      categoryUtility: '交易通知',
      categoryUtilityHint: '订单／预约相关，24 小时对话期内免费。',
      categoryMarketing: '推广消息',
      categoryMarketingHint: '优惠及宣传内容，按条收费。',
      bodyLabel: '消息内容',
      variablesLabel: '变量',
      languageLabel: '语言',
      wabaLabel: 'WhatsApp Business 账户',
      lastSynced: '最后同步 {date}',
      rejectedReason: '拒绝原因',
      viewDetail: '查看详情',
      detailMetaName: 'Meta 模板名称',
      newTemplate: '新增模板',
      editTemplate: '修改',
      systemBadge: '系统通知',
      systemEditHint:
        '这是平台预设文案。一旦你修改，这个模板就不会再跟系统日后的更新。',
      editTemplateLang: '修改{language}版',
      pickerNoApproved: '尚无已通过审核的模板。请先创建模板并等待 Meta 审核通过。',
      pickerAutoFilled: '按收件人自动填入',
      pickerLanguageAuto: '语言会按每位客人的偏好自动选择',
      langNames: {
        zh_HK: '繁体中文',
        zh_CN: '简体中文',
        en: '英文',
      },
      addTranslation: '新增翻译',
      variantAdded: '{language}版本已提交审核',
      variantAddFailed: '未能新增翻译，请重试。',
      languageExists: '此模板已有该语言版本。',
      otherLanguages: '其他语言',
      searchLanguage: '搜索语言…',
      noLanguageResults: '找不到匹配的语言',
      operatingLanguages: '运营语言',
      operatingLanguagesHint:
        '你为客人提供服务的语言。模板覆盖率、翻译建议和默认发送都跟随这个列表 — 第一个是主要语言。',
      primaryLang: '主要',
      makePrimary: '设为主要语言',
      addLanguage: '新增语言',
      removeLangTitle: '移除{language}？',
      removeLangBody: '此语言已审批的模板会保留、仍可发送 — 只是不再计入覆盖率。',
      provisionSystemCheckbox: '同时一键提交所有系统模板的{language}版本给 Meta 审核（建议）',
      provisionNonRegistryHint:
        '系统模板可以在每张模板卡逐一新增此语言版本 — AI 翻译会起草内容供你审阅。',
      insightTitle: '语言洞察',
      insightLine: '有 {n} 位客人偏好{language}，你暂未支持此语言。',
      insightAdd: '加入{language}',
      insightDismiss: '略过',
      bulkFillLine: '{n} 个系统模板已备有{language}官方文案，可提交 Meta 审核。',
      bulkFillCta: '查看并提交',
      bulkFillProgress: '提交中 {i}/{n}…',
      bulkFillDone: '已提交 {n} 个模板给 Meta 审核。',
      bulkFillPartial: '已提交 {ok} 个，{fail} 个失败 — 可在模板卡逐个重试。',
      bulkFillNonRegistryHint:
        '{n} 个系统模板尚无{language}版本 — 请在各模板卡逐一新增（AI 翻译会起草内容）。',
      bulkReviewTitle: '提交 {n} 个{language}系统模板',
      bulkReviewNote:
        '以上是系统官方文案，提交后由 Meta 审核（一般数分钟至数小时），批准后对应功能自动生效。',
      bulkReviewConfirm: '提交 {n} 个给 Meta 审核',
      initializing: '系统模板初始化中，正在你的 WhatsApp 账户建立…',
      ghostTemplatesNote: '另有 {n} 个模板属于已断开的 WhatsApp 号码，未在此显示。',
      provisionAfterConnectHint: '连接 WhatsApp 后，系统模板会自动以你的运营语言提交审核。',
      authExpired: 'WhatsApp 授权已失效，模板无法同步或发送 — 请重新连接你的 WhatsApp 账户。',
      detailPreviewLabel: '客人会收到的消息',
      technicalDetails: '技术资料',
      qualityGood: '良好',
      qualityFair: '一般',
      qualityPoor: '欠佳',
      lastSyncedLabel: '最后同步',
      pickerNoMarketing: '你还没有已审批的推广模板 — 群发必须使用「推广」类别的模板才可以发送。',
      customEmptyTitle: '你的推广消息模板',
      customEmptyBody:
        '客人超过 24 小时没有回复后，WhatsApp 只允许发送经 Meta 审批的模板。群发、自动化流程和 AI 助手跟进都会从这里取模板 — 建立你自己的推广内容吧。',
      addVariantTitle: '新增{language}版本',
      addVariantReviewNote: '提交后会将此内容送交 Meta 审核（一般数分钟至数小时），批准后自动生效。',
      addVariantSubmit: '提交给 Meta 审核',
      addVariantGoEditor: '前往编辑器',
      addVariantEditorHint: '此版本需在编辑器撰写 — AI 翻译会由现有版本起草内容供你审阅后再提交。',
      manageLanguages: '管理',
      removeLangAction: '移除',
      system: {
        booking_confirmation: {
          title: '预约确认通知',
          trigger: '客人成功预约后立即自动发送',
        },
        appointment_reminder_24h: {
          title: '预约前一天提醒',
          trigger: '预约前 24 小时自动发送',
        },
        appointment_reminder_2h: {
          title: '预约前两小时提醒',
          trigger: '预约前 2 小时自动发送',
        },
        order_confirmed: {
          title: '订单确认和付款指示',
          trigger: '客人下单后立即自动发送',
        },
        payment_received: {
          title: '收到付款通知',
          trigger: '你确认收到付款后自动发送',
        },
        payment_reminder: {
          title: '付款提醒',
          trigger: '预付订单 4 小时后仍未付款时自动发送（只在你当地时间 09:00–21:00 发送）',
        },
        order_cancelled: {
          title: '订单取消通知',
          trigger: '订单被取消时自动发送',
        },
        review_request: {
          title: '邀请客人留评价',
          trigger: '完成预约或订单后自动发送',
        },
        booking_slot_available: {
          title: '候补有位通知',
          trigger: '有人取消、空出候补客想要的时段时，由你发出',
        },
        product_back_in_stock: {
          title: '货品到货通知',
          trigger: '客人等候的货品到货时自动发送',
        },
      },
      groups: {
        booking: '预约通知',
        orders: '订单和付款',
        relationship: '客户关系',
        custom: '你写的消息',
        customHint: '群发、自动化流程和 AI 跟进都从这里发。',
        customTrigger: '由你自己发 — 群发、自动化流程、AI 跟进',
        notUsedByYourBusiness: '{group} — {n} 个你的业务暂时用不上',
      },
      state: {
        working: '运作中',
        workingLang: '运作中（{languages}）',
        inReview: 'Meta 审核中 · 一般数分钟至 24 小时',
        fallbackWarn: '{missing}客人会收到{fallback}',
        fallbackFix: '加{language}版',
        cannotSend: '无法发送 — 尚未有任何语言获批',
        cannotSendFix: '立即设置',
        rejected: '被 Meta 拒绝 — 需要修改',
        rejectedFix: '修改并重新提交',
      },
      attention: {
        title: '{n} 个通知需要处理',
        filterAll: '全部（{n}）',
        filterNeedsAction: '需要处理（{n}）',
        allClear: '所有通知都运作中',
      },
      cost: {
        free: '免费',
        freeHint:
          '订单、预约相关通知。客人 24 小时内回过消息就免费。',
        paid: '每条收费',
        paidHint: '推广内容。Meta 会按发送数量收费。',
      },
      intro: {
        title: '为什么 WhatsApp 要事先审批消息？',
        body1:
          '客人超过 24 小时没回复之后，WhatsApp 只允许你用事先经 Meta 审批的内容去找他。',
        body2:
          '这里列出的就是那些内容。每个只需审批一次（一般数分钟至 24 小时），之后可以一直发。',
        body3: '订单和预约通知免费；推广消息每条收费。',
        dismiss: '明白了',
      },
      searchPlaceholder: '搜索通知…',
      noSearchResults: '没有符合的通知',
      editor: {
        newTitle: '新增 WhatsApp 模板',
        editTitle: '修改模板',
        displayName: '模板名称',
        displayNamePlaceholder: '例如：生日优惠通知',
        categoryLabel: '用途',
        bodyPlaceholder: '输入消息内容。请用下方按钮插入变量，不需手动输入 {{1}}。',
        insertVariable: '插入变量',
        customVariable: '自定义…',
        customVariablePrompt: '变量名称（小写英文字母／下划线，例如 coupon_code）',
        footerLabel: '页脚（可选）',
        footerPlaceholder: '例如：回复「停止」即可取消接收',
        buttonsLabel: '快速回复按钮（可选）',
        buttonsHint: '最多 3 个按钮，每个 25 字以内。',
        addButton: '新增按钮',
        samplesTitle: '变量示例值',
        samplesHint: '供 Meta 审核人员参考，并用于预览。',
        previewTitle: '预览',
        submit: '提交审核',
        submitting: '提交中…',
        resubmit: '保存并重新提交审核',
        reviewNote: 'Meta 审核通常需时几分钟至 24 小时，结果会以通知告知你。',
        editQuotaNote: '已通过的模板每 24 小时只可修改一次（每 30 天最多 10 次）。审核期间旧版本会继续生效。',
        pendingLocked: '此模板审核中，须待 Meta 有结果后才能修改。',
        deleteTemplate: '删除模板',
        deleteConfirmTitle: '确定删除此模板？',
        deleteConfirmBody: '模板会同时从 Meta 移除，使用此模板的群发及自动化将无法发送。',
        metaRejected: 'Meta 拒绝了提交：{message}',
        unknownVariables: '系统模板只可使用内置变量：{allowed}',
        submitted: '已提交审核',
        variantTitle: '新增翻译',
        variantSubtitle: '{key} · {language}',
        variantNote: '翻译版本沿用原模板的变量，只需改写文字内容。每种语言由 Meta 独立审核。',
        translateFrom: '✨ AI 由{language}翻译',
        moreLanguages: '更多语言…',
        translating: '翻译中…',
        aiDraftTitle: 'AI 帮我写',
        aiDraftHint: '一句话说出你想发什么，AI 会起草一个合规的推广模板，你审阅修改后再提交。',
        aiDraftPlaceholder: '例如：新店开张全单 8 折，请客人回复预约',
        aiDraftCta: '起草',
        aiDraftLoading: '起草中…',
        aiDraftFailed: 'AI 起草失败，请再试一次或自行撰写。',
        translateFailed: 'AI 翻译失败，请手动输入或重试。',
        variablesMismatch: '变量必须与原模板一致：{expected}',
        sectionBasics: '基本资料',
        sectionContent: '消息内容',
        sectionChecklist: '提交前检查',
        startTitle: '想怎样开始？',
        startHint: '之后全部都可以改——未按提交之前，不会送去 Meta。',
        startAi: 'AI 帮我写',
        startAiHint: '一句说出你想发什么',
        startBlank: '自己写',
        startBlankHint: '由空白消息开始',
        startCopy: '复制现有模板',
        startCopyHint: '由你已有的模板开始',
        copySourceTitle: '要复制哪个模板？',
        copySourceEmpty: '暂时没有可复制的模板。',
        aiRewrite: '用 AI 重写',
        aiOverwriteTitle: '要覆盖你已写的内容？',
        aiOverwriteBody: 'AI 草稿会取代现有的消息、页脚和按钮。完成后可以还原一次。',
        aiOverwriteConfirm: '覆盖',
        aiDraftBadge: 'AI 草稿 · 请检查',
        aiDraftUndo: '还原',
        metaNamePreview: 'Meta 名称：{name}',
        metaNameLocked: '提交后不可更改。',
        metaNameFallback: 'Meta 名称只接受英文字母，所以会变成「{name}」。建议加几个英文字，方便日后辨认。',
        headerLabel: '标题（选填）',
        headerHint: '消息上方一行粗体短句。标题不支持变量。',
        headerNone: '不使用',
        headerText: '文字',
        headerPlaceholder: '例如：你的预约已确认',
        buttonsHintV2: '最多 3 个快速回复，或者 1 个网址 + 1 个致电按钮。WhatsApp 不容许两种混用。',
        buttonTypeQuickReply: '快速回复',
        buttonTypeUrl: '网址',
        buttonTypePhone: '致电',
        buttonLabelPlaceholder: '按钮文字',
        buttonUrlPlaceholder: 'https://example.com/book',
        buttonPhonePlaceholder: '+85298765432',
        removeButton: '移除按钮',
        variableGroupCustomer: '顾客',
        variableGroupCommerce: '订单',
        variableGroupBooking: '预约',
        variableGroupOther: '其他',
        customVariableTitle: '自定义变量',
        customVariableLabel: '变量名称',
        customVariableInvalid: '只可用小写英文、数字和下划线，并以英文字母开头——例如 coupon_code。',
        customVariableAdd: '插入',
        samplesInlineHint:
          'Meta 审核员会看到这些示例值，预览也会使用它们。留空就会用预设示例 — 真实内容发送时会逐个客人代入。',
        checklistPassed: '已通过 {n} 项',
        checklistErrors: '{n} 项须修正',
        checklistWarnings: '{n} 项建议',
        checklistAllGood: '全部检查通过，可以提交。',
        checklistFix: '前往修正',
        submitBlocked: '还有 {n} 项要修正',
        previewShowSamples: '示例值',
        previewShowVariables: '变量名称',
        previewBillingMarketing: '推广模板——每条发送计费。',
        previewBillingUtility: '交易通知——24 小时对话窗内免费。',
        previewCompareSource: '原文（{language}）',
        rejectionTitle: 'Meta 拒绝了这个模板',
        rejectionReasonLabel: 'Meta 给出的原因',
        rejectionFixTitle: '通常怎样修正',
        pendingTitle: 'Meta 审核中',
        backToList: '返回模板列表',
        draftRestoreTitle: '要恢复未提交的草稿？',
        draftRestoreBody: '你上次离开时还有未提交的修改。可以恢复，或者重新开始。',
        draftRestore: '恢复草稿',
        draftDiscard: '重新开始',
        draftSaved: '草稿已自动保存在此设备',
        leaveTitle: '未提交就离开？',
        leaveBody: '这个模板未提交去 Meta。草稿会保留在这台设备。',
        leaveStay: '继续编辑',
        leaveConfirm: '离开',
        lint: {
          bodyEmpty: '消息内容不能为空。',
          bodyTooLong: '内容超过 1,024 字上限。',
          startsWithVariable: 'Meta 会拒绝以变量开头的模板——请在前面加上文字。',
          endsWithVariable: 'Meta 会拒绝以变量结尾的模板——请在后面加上文字。',
          adjacentVariables: '两个变量不能相连——请在中间加上文字。',
          variableDensity: '变量比例过高的消息较易被拒——建议加入更多固定文字。',
          headerTooLong: '标题超过 60 字上限。',
          headerHasVariable: '标题必须是固定文字——请将变量移到消息内容。',
          footerTooLong: '页脚超过 60 字上限。',
          buttonTooLong: '按钮文字上限为 25 字。',
          buttonTextEmpty: '每个按钮都需要文字。',
          tooManyButtons: '快速回复按钮最多 3 个。',
          tooManyCtaButtons: '最多只可有一个网址按钮和一个致电按钮。',
          mixedButtonTypes: '快速回复不能与网址／致电按钮混用——请只选一种。',
          duplicateButtonText: '有两个按钮文字相同——Meta 要求每个按钮文字不同。',
          buttonUrlInvalid: '请输入完整网址，以 https:// 开头。',
          buttonPhoneInvalid: '请连国家区号一并输入，例如 +85298765432。',
          missingSamples: '每个变量都需要提供示例值。',
          marketingNoOptOut: '推广模板加入取消接收提示（例如「回复停止即可取消」）较易通过审核。',
          categoryMismatch: '这个内容像推广消息，但类别选了交易通知——Meta 很常因此拒绝。建议改为推广，或者收敛用词。',
        },
        rejection: {
          invalidFormat: '模板格式无效——通常是变量开头／结尾、变量相连，或变量与示例值不符。',
          tagContentMismatch: '内容与所选类别不符——例如交易通知类别出现推广字眼。请更改类别或修改内容。',
          abusiveContent: 'Meta 判定内容违反其政策，请检查用字。',
          scam: 'Meta 怀疑内容涉及诈骗。请移除催逼字眼及不清晰的优惠描述。',
          generic: '请对照 WhatsApp 模板政策检查内容，修改后重新提交。',
        },
        presetVars: {
          customer_name: '顾客名称',
          business_name: '商户名称',
          order_number: '订单编号',
          service_name: '服务名称',
          product_name: '商品名称',
          date: '日期',
          time: '时间',
          amount: '金额',
          reference: '参考编号',
          review_url: '评价链接',
          payment_instructions: '付款方式',
          reason: '原因',
        },
      },
    },
    payment: {
      onlinePlatforms: '在线收款平台',
      onlinePlatformsDesc: '连接收款平台后，客户在网店或与 AI 对话下单时，都会自动生成付款链接',
      stripeDesc: '信用卡、Apple Pay、Google Pay',
      stripePayments: '支持信用卡、Apple Pay、Google Pay 等',
      airwallexDesc: '支持多币种收款，适合跨境商户',
      manualMethods: '手动付款方式',
      manualMethodsDesc: '客户用这些方式离线付款。确认订单后，系统会自动发送付款指引给客户。',
      methodInstructionsPlaceholder: '付款指引（例如：账号、转账详情）',
      addMethod: '添加付款方式',
      manualHint: '您需要自行确认收款并更新订单状态。',
      vacationMode: '休假模式',
      vacationModeDesc: '开启后，AI 不接受新订单或预约',
      returnDate: '预计返回日期',
      vacationMessage: '给客户的留言',
      vacationMessagePlaceholder: '例如：我们周一回来！',
      flowTitle: '收款流程',
      flowDesc: '下单时如何处理付款',
      flowStep1: 'AI 帮客户下单后，如果已连接在线收款平台，会自动生成付款链接发给客户。',
      flowStep2: '客户付款后，订单自动更新为已付款，同时通知商户。',
      flowStep3: '若未连接在线平台，客户会看到您设置的手动付款方式，并按照指引付款。商户需自行确认收款并更新订单状态。',
      stripeConnected: 'Stripe 已连接',
      stripeSetupIncomplete: 'Stripe 设置未完成',
      stripeEnabled: '已启用',
      stripeSetup: '设置中',
      stripeVerified: '✓ 账户已验证',
      stripeVerifying: '⚠ 账户验证中',
      stripeChargesEnabled: '✓ 可接收付款',
      stripeChargesDisabled: '⚠ 收款功能未启用',
      stripePayoutsEnabled: '✓ 自动提款已开启',
      stripePayoutsHint: '提款需到账户设置',
      stripeContinue: '继续设置',
      stripeDisconnect: '断开连接',
      stripeDisconnecting: '断开中...',
      stripeDisconnectConfirm: '确定要断开 Stripe 连接？断开后客户将无法通过在线付款链接付款。',
      stripeConnect: '连接',
      stripeConnecting: '连接中...',
      stripeDashboard: '管理 Stripe',
      stripeCollapse: '收起管理面板',
      stripeLoadingPanel: '加载 Stripe 面板...',
      stripeLoadError: '无法加载 Stripe 面板',
      stripeMissingKey: 'Stripe publishable key 未设置',
      stripeTabs: { payments: '交易记录', payouts: '提款', balances: '余额', account: '账户设置' },
    },
    team: {
      title: '团队',
      desc: '管理团队成员及权限',
      inviteTitle: '邀请成员',
      invitePlaceholder: '邮箱地址',
      invite: '邀请',
      inviting: '邀请中...',
      pendingInvites: '待接受邀请',
      expires: '到期：',
      copyLink: '复制链接',
      copied: '已复制',
      removeConfirm: '确定要移除该成员？',
      revokeConfirm: '确定要撤销该邀请？',
      you: '你',
    },
    notifications: {
      inviteLinkHint: '将此链接发送给对方，对方登录后会自动加入你的团队。',
      inviteLinkExpiry: '邀请后会生成链接，你可以{copyLink}发送给对方。邀请有效期 7 天。',
    },
    whatsapp: {
      title: 'WhatsApp 连接',
      desc: '连接你的 WhatsApp Business 账户，AI 客服会自动回复客户消息',
      connected: 'WhatsApp 已连接',
      phoneNumber: '电话号码：',
      displayName: '显示名称：',
      linkedAt: '连接时间：',
      features: 'AI 客服功能：',
      featuresItems: {
        autoReply: '自动回复客户 WhatsApp 消息',
        productOrders: '处理产品查询和下单',
        bookingManagement: '预约管理',
        mediaUnderstanding: '图片和语音消息理解',
      },
      disconnect: '断开 WhatsApp',
      disconnecting: '断开中...',
      disconnectConfirm: '确定要断开 WhatsApp 连接？断开后 AI 客服将无法收发 WhatsApp 消息。',
      connectTitle: '连接 WhatsApp Business',
      steps: {
        step1: '步骤 1',
        step1Desc: '点击下方按钮开始连接',
        step2: '步骤 2',
        step2Desc: '登录你的 Facebook 账户并选择 WhatsApp Business 账户',
        step3: '步骤 3',
        step3Desc: '确认连接后，AI 客服会自动开始运行',
      },
      connectButton: '连接 WhatsApp',
      fbSuccess: 'Facebook 授权成功！',
      fbSuccessDesc: '请输入你的 WhatsApp Business Account ID 和 Phone Number ID 完成设置。你可以在 developers.facebook.com → 你的 App → WhatsApp → API Setup 找到这些信息。',
      wabaId: 'WhatsApp Business Account ID',
      phoneNumberId: 'Phone Number ID',
      verifying: '验证中...',
      completeSetup: '完成设置',
      metaNotConfigured: 'Meta App ID 未设置，请联系管理员。',
      fbNotLoaded: 'Facebook SDK 未加载，请刷新页面。',
      setupFailed: '设置失败，请重试。',
      errors: {
        TOKEN_EXCHANGE_FAILED: '无法完成 Facebook 授权，请重新尝试连接。',
        SIGNUP_FAILED: 'WhatsApp 连接失败，请重试。',
        NO_PENDING_TOKEN: '找不到 Facebook 授权，请先完成「连接 WhatsApp」步骤。',
        WABA_VERIFY_FAILED: '无法验证 WABA ID。请确认 ID 正确，并且你已完成 Facebook 授权。',
        PHONE_NOT_IN_WABA: 'Phone Number ID 不属于此 WABA。可用的电话号码：{availablePhones}',
        NUMBER_IN_USE: '此号码已连接到另一个 NovixOne 账户。请先在该账户中断开连接，或联系客服。',
        WABA_NO_PHONES: '此 WABA 下找不到任何电话号码。请先于 Meta Business Suite 新增电话号码。',
      },
    },
    billing: {
      title: '套餐 & 账单',
      desc: '管理你的订阅套餐',
      loading: '加载中...',
      loadFailed: '加载失败',
      currentPlan: '当前套餐',
      manage: '管理订阅',
      used: '已使用',
      resets: '下次重置：',
      choosePlan: '选择套餐',
      popular: '推荐',
      current: '当前套餐',
      currentPlan2: '当前套餐',
      perMonth: '月',
      processing: '处理中...',
      upgrade: '立即升级',
      downgrade: '降级',
      enterpriseDesc: '无限 AI credits、专属客户经理、自定义集成',
      contactUs: '联系我们',
      subscriptionSuccess: '订阅成功！你的套餐已更新。',
      credits: {
        title: 'AI Credits',
        desc: '一个额度通用所有 AI 功能 — AI 回复、Copilot、知识库、洞察都由此扣除。',
        totalRemaining: '剩余 credits',
        monthlyGrant: '每月套餐 credits',
        purchasedBalance: '加购 credits（永不过期）',
        replyAnchor: '1 条 AI 回复 ≈ {n} credits',
        burnTitle: '用量 — 最近 {days} 天',
        burnEmpty: '此期间尚无 AI 用量。',
        burnSummary: '过去 {days} 天已使用 {count} credits',
        unit: 'credits',
        groupLabels: {
          customer_ai: '客服 AI',
          copilot: 'Copilot',
          knowledge: '知识库',
          insights: '洞察',
          drafts: '草稿',
          embeddings: '搜索',
          other: '其他',
        },
        packsTitle: '购买 Credits',
        packsDesc: '一次性购买，永不过期。',
        packCredits: '{n} credits',
        packBuy: '购买',
        purchaseSuccess: 'Credits 购买成功！已加入你的余额。',
        // Usage & limits (mobile parity with /usage — RFC usage-revamp Phase 5)
        forecastUnlimited: '无上限方案，无需预测。',
        forecastInsufficient: '数据不足 3 天，暂时无法预测。',
        forecastOk: '按这个速度，期末大约用到方案 {pct}%。',
        forecastTight: '按这个速度，期末会用到方案 {pct}%，建议留意。',
        forecastRunout: '按这个速度，{date}（{days} 天后）会用完所有 credits。',
        forecastPace: '最近 7 天平均每天 {n} credits',
        daysLeft: '本期还剩 {n} 天',
        macLabel: '本月活跃客户',
        macNoSnapshot: '首次统计后显示',
        limitsTitle: '方案限额',
        limitsSubtitle: '与 paywall 使用同一组数字',
        limitsShow: '显示限额',
        limitsHide: '收起',
        limitsUnlimited: '无限制',
        limitsNotMeasured: '未测量',
        limitsNotEnforced: '未生效',
        limitsAtCap: '已达上限',
        limitLabels: {
          monthlyActiveCustomers: '本月活跃客户',
          maxTeamMembers: '团队成员',
          maxWhatsappNumbers: 'WhatsApp 号码',
          maxProducts: '商品 / 服务',
          maxKnowledgeEntries: '知识库条目',
          maxCustomerSegments: '客户分群',
          maxAutomationRules: '自动化规则',
          maxLocations: '分店',
          maxCouponCampaigns: '优惠券活动',
          maxCustomFieldsPerObject: '自定义字段（每个对象）',
          webChatMaxOrigins: '网站聊天域名',
          monthlyWaTemplateBroadcasts: '本月 WhatsApp 群发',
          monthlyEmailBroadcasts: '本月邮件群发',
          monthlyAutomationExecutions: '本月自动化执行',
          maxCouponCodesPerBatch: '每批优惠码上限',
          mediaStorageMb: '媒体存储空间',
          mediaRetentionDays: '媒体保留期',
          auditRetentionDays: '审计记录保留期',
        },
      },
      planBullets: {
        free: [
          '每月 50K AI credits',
          '1 个 WhatsApp 号码',
          '最多 2 位团队成员',
          '无限商品、50 条知识库（可从网站 / PDF 导入）',
          '基本客户 CRM + 订单管理',
          '手机 App + 0% Stripe Connect 手续费',
        ],
        starter: [
          '每月 250K AI credits',
          '1 个 WhatsApp 号码 · 5 位团队成员',
          '无限商品、500 条知识库',
          'WhatsApp 群发每月 500 位收件人',
          '邮件群发每月 500 位收件人',
          '包含所有 Free 套餐功能',
        ],
        pro: [
          '每月 1M AI credits',
          '3 个 WhatsApp 号码 · 15 位团队成员',
          '最多 3 间分店、5,000 条知识库',
          '群组 / 漏斗分析',
          'WhatsApp 群发每月 5,000 位收件人',
          '包含所有 Starter 套餐功能',
        ],
        business: [
          '每月 4M AI credits',
          '10 个 WhatsApp 号码 · 50 位团队成员',
          '最多 10 间分店、无限知识库',
          '自定义角色 + 权限管理 · 自定义域名',
          '365 日审计记录',
          '包含所有 Pro 套餐功能',
        ],
        enterprise: [
          '无限 AI credits · 无限团队成员',
          '用量上限及审计保留期按合同约定',
          '数据处理协议 (DPA)',
          '专属客户经理 + SLA',
          '可协商独立部署',
        ],
      },
    },
  },

  mobile: {
    tabs: {
      home: '首页',
      conversations: '对话',
      customers: '客户',
      business: '业务',
      more: '设置',
    },
    datetime: {
      justNow: '刚刚',
      today: '今天',
      tomorrow: '明天',
      yesterday: '昨天',
    },
    settings: {
      closeAccount: {
        title: '关闭账户',
        intro: '关闭后店铺立即停用，宽限期过后永久删除所有客户、对话、订单和预约。审计记录会为合规保留。',
        graceHint: '{days} 天宽限期 — 期内随时可以取消。',
        confirmLabel: '输入店名（{name}）确认',
        confirmPlaceholder: '店名',
        button: '关闭账户',
        working: '处理中…',
        failed: '无法开始关闭流程',
        nameMismatch: '店名不符。',
        confirmAlertTitle: '确定关闭账户？',
        confirmAlertBody: '所有同事立即无法再使用这家店。宽限期内可以取消。',
        confirmAlertCancel: '保留账户',
        confirmAlertConfirm: '关闭账户',
        pendingTitle: '已安排关闭 — 数据将于 {date} 删除',
        pendingBody: '店铺已停用。取消即可重新启用并保留所有数据。',
        cancelButton: '取消关闭',
        cancelling: '取消中…',
        cancelFailed: '取消失败',
        ownerOnly: '只有账户所有者可以关闭账户。',
      },
      region: {
        title: '地区',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        introHeading: '你主要在哪里做生意？',
        introBody: '这会影响电话区号预设、付款方式建议与未来地区化的功能。修改不会影响时区或语言。',
        tapToChange: '点击更改',
        unsavedNotice: '尚未保存：将由「{from}」变更为「{to}」',
      },
      profile: {
        title: '个人资料',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        introHeading: '只属于你的账户',
        introBody: '这页的设置只影响你自己的登录体验，不会影响商家或其他同事。',
        displayNameLabel: '显示名称',
        displayNamePlaceholder: '例如：陈小明',
        displayNameHint: '通知、系统记录与团队画面都会看到你的名字。',
        emailLabel: '登录邮箱',
        emailSignedOut: '尚未登录',
        emailHint: '邮箱是登录身份，目前不支持在 app 中修改。',
      },
      timezone: {
        title: '时区',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        introHeading: '选择你做生意的时区',
        introBody: '影响「今日」「本周」的分界、预约提醒与自动跟进的排程。',
        currentSelection: '当前选择',
        searchPlaceholder: '搜索城市或 UTC 偏移',
        emptyResults: '没有搜索结果',
        regions: {
          asia: '亚洲',
          pacific: '太平洋',
          europe: '欧洲',
          americas: '美洲',
          africa: '非洲',
          middleEast: '中东',
        },
      },
      account: {
        title: '账户资料',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        phoneInvalidTitle: '电话格式错误',
        phoneInvalidBody: '请检查电话号码。',
        introHeading: '你的生意身份',
        introBody: '这些资料会出现在收据、AI 自动回复与客户看到的地方。写得越清楚，AI 越能用你的口吻。',
        nameLabel: '商家名称',
        namePlaceholder: '例如：晴峰空调工程',
        nameHint: '会出现在收据与 AI 介绍自己时。',
        typeLabel: '生意类型',
        typePlaceholder: '例如：物理治疗中心、家居用品零售、室内设计',
        descLabel: '店铺简介',
        descLabelHint: '一两句话介绍你的业务',
        descPlaceholder: '例如：在铜锣湾及荃湾设有门店的家居用品零售店，也通过网店及 WhatsApp 接单。',
        phoneLabel: '联系电话',
        phoneHint: '你自己的电话 — AI 转介客户时会联系你。与客户的 WhatsApp 号码无关。',
        nameRequired: '商家名称不可留空。',
      },
      notifications: {
        title: '通知偏好',
        loadError: '暂时无法加载通知设置',
        introHeading: '选择你想收到通知的方式',
        introBody: '这些是你个人的设置 — 只影响你自己如何收到通知，与其他同事或商家设置无关。',
        footnote: '邮件通知可能会延迟几分钟。',
        events: {
          escalation: {
            title: '需要接管',
            description: 'AI 处理不了、客户坚持要找人，第一时间通知你。',
          },
          newOrder: {
            title: '新订单',
            description: '新订单时通知（不论是通过网店、AI 对话还是会员中心）。',
          },
          appointment: {
            title: '预约提醒',
            description: '新预约、改期、24 小时 / 2 小时前提醒。',
          },
          inquiry: {
            title: '客户咨询',
            description: '投诉、问题或新咨询收到时提醒你。',
          },
          followUp: {
            title: '跟进到期',
            description: '你安排的跟进到期、或者逾期时提醒你。',
          },
          inventory: {
            title: '库存偏低',
            description: '每日汇总跌到低库存线或以下的商品。',
          },
          integrationApproval: {
            title: '待批准操作',
            description: 'AI 想在你自己的系统执行操作，需要同事批准。只通知有权批准的人，静音时段也会照常通知。',
          },
          channel: {
            title: 'WhatsApp 连接状态',
            description: '当 Meta 中断您其中一个 WhatsApp 号码，或号码恢复连接时通知您。号码中断期间，AI 无法回复该号码的客人。静音时段也会照常通知。',
          },
          dailyReport: {
            title: '每日摘要',
            description: '每日下午 8 点收一次今日生意的小结。',
          },
        },
        channels: {
          webPush: '推送',
          email: '邮件',
        },
      },
      hours: {
        title: '营业时间',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        loadError: '加载失败',
        timeFormatTitle: '时间格式错误',
        timeFormatBody: '{day} 的时间要是 HH:MM 格式（例如 09:00）',
        timeOrderTitle: '时间错误',
        timeOrderBody: '{day} 的结束时间要晚于开始时间',
        introHeading: '设置每日营业时间',
        introBody: '营业时间会用在网店、预约系统和 AI 回复 — 例如非营业时间的自动回复，或提示下单截止时间。',
        days: {
          monday: { label: '星期一', short: '一' },
          tuesday: { label: '星期二', short: '二' },
          wednesday: { label: '星期三', short: '三' },
          thursday: { label: '星期四', short: '四' },
          friday: { label: '星期五', short: '五' },
          saturday: { label: '星期六', short: '六' },
          sunday: { label: '星期日', short: '日' },
        },
        closed: '休息',
        toSeparator: '至',
        timezoneNote: '时区：{tz}',
      },
      categories: {
        title: '商品分类',
        introHeading: '商品分类',
        introBody: '用分类整理你的商品与服务，方便管理与指派人员。',
        emptyTitle: '暂无分类',
        emptySub: '添加分类帮你整理商品，例如「美甲」、「护理」、「按摩」。',
        deactivated: '已停用',
        failed: '失败',
        sheetTitleCreate: '添加分类',
        sheetTitleEdit: '编辑分类',
        nameLabel: '名称',
        namePlaceholder: '例如：美甲 / 按摩 / 护理',
        descLabel: '描述',
        descPlaceholder: '选填 — 简短描述这个分类',
        saveCreate: '建立分类',
        saveEdit: '保存更改',
        deleteBtn: '删除分类',
        deleteConfirmTitle: '删除分类？',
        deleteConfirmBody: '删除「{name}」— 里面的商品不会被删除，只是不再归类。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '删除',
      },
      outreach: {
        title: '自动跟进',
        loadError: '暂时无法加载跟进设置',
        introHeading: '让 AI 主动帮你联系客户',
        introBody: '开启后，AI 会在合适的时间自动发送 WhatsApp 消息跟进 — 你不需要再记住谁该 follow up。',
        footnote: 'WhatsApp 规定：自动跟进消息要使用 Meta 已批准的模板。开启这里之后，还要在网页版「自动跟进」确认模板设置好才会真正发送。',
        triggers: {
          abandonedInterest: {
            title: '咨询未下单',
            tagline: '隔日轻推一下',
            description: '客户问过价格、看过产品但未下单 — AI 会隔一天用你批准的消息模板再 follow up 一次，减少流失。',
            cadence: '触发后 24 小时内 · 每位客户最多 7 天 1 次',
          },
          lapsedCustomer: {
            title: '老客户安静了',
            tagline: '主动 Win-back',
            description: '购买过的客户 14 天没动静 — AI 会提醒你记住他们，自动发一个温暖的问候消息，试着带回他们做下一单。',
            cadence: '安静 14 天后 · 每位客户最多 30 天 1 次',
          },
          postOrder: {
            title: '下单之后',
            tagline: '完成后再问候',
            description: '订单完成或预约做完 72 小时后，AI 会自动问候客户，确认一切顺利 — 顺手拿好评，建立长期关系。',
            cadence: '完成后 72 小时 · 每位客户最多 14 天 1 次',
          },
        },
      },
      policies: {
        title: '政策',
        save: '保存',
        saveError: '保存失败',
        saveSuccessTitle: '已保存',
        saveSuccessBody: '政策已更新。',
        unknownError: '未知错误',
        sectionBooking: '预约取消政策',
        sectionOrder: '订单取消政策',
        sectionVacation: '休假模式',
        allowCancelBooking: '允许客户取消预约',
        allowCancelOrder: '允许客户取消订单',
        windowLabel: '取消期限',
        windowBookingOption: '{hours} 小时前',
        windowOrderOption: '{hours} 小时内',
        windowBookingHint: '客户要在预约前几小时取消才有退款',
        windowOrderHint: '客户下单后几小时内可以取消',
        refundLabel: '退款方式',
        refundFull: '全额退款',
        refundPartial: '部分退款',
        refundNone: '不退款',
        refundPercentLabel: '退款比例',
        blockIfShipped: '已发货后不可取消',
        vacationToggle: '启用休假模式',
        vacationHint: '开启后 AI 不会处理新订单或预约，只会回复你设置的消息。',
        vacationReturnDate: '返回日期',
        vacationMessage: '休假消息',
        vacationMessagePlaceholder: '例如：我们放假中，1月3号回来，到时见！',
        notConfigured: '尚未设置取消政策',
        notConfiguredHint: 'AI 会把所有取消请求转给你人工处理。设置政策后 AI 可自动处理符合条件的取消。',
        configureButton: '设置政策',
        clearButton: '清除政策',
      },
      soul: {
        title: 'AI 个性',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        loadError: '加载失败',
        introHeading: '教 AI 像你一样',
        introBody: '挑一个语气，添加几条一定要做的守则与绝不能做的红线。AI 每次回客都会遵循。',
        sectionTone: '语气',
        sectionGuidelines: '守则',
        sectionRedLines: '红线',
        guidelinesHint: 'AI 一定要做的事，例如：「永远先问客户名」、「报价要含运费」',
        redLinesHint: 'AI 绝对不能做的事，例如：「不要随便打折」、「不要回答医疗问题」',
        guidelinePlaceholder: '添加一条守则',
        redLinePlaceholder: '添加一条红线',
        guidelinesEmptyTitle: '暂无守则',
        guidelinesEmptySub: '添加一条教 AI 如何接客',
        redLinesEmptyTitle: '暂无红线',
        redLinesEmptySub: '添加一条保护你的品牌',
        tones: {
          friendly: { label: '亲切', sub: '温暖、近距离' },
          professional: { label: '专业', sub: '正式、有礼' },
          casual: { label: '轻松', sub: '随意、像朋友' },
          enthusiastic: { label: '热情', sub: '活泼、有活力' },
        },
      },
      availability: {
        title: '可预约时段',
        saveSettings: '保存设置',
        saveSchedule: '保存时段',
        saveError: '保存失败',
        unknownError: '未知错误',
        settingsSavedTitle: '已保存',
        settingsSavedBody: '预约设置已更新。',
        scheduleSavedTitle: '已保存',
        scheduleSavedBody: '每周时段已更新，新时段会即时用在网店、AI 预约和手动添加。',
        blockFailedTitle: '添加失败',
        sectionSettings: '预约设置',
        sectionWeekly: '每周时段',
        sectionBlocked: '封锁日期',
        staffModeLabel: '服务人员分派',
        staffModes: {
          none: { label: '不需要', sub: '使用全局容量，不分派人员' },
          auto: { label: '自动分派', sub: '系统自动分派服务人员' },
          choice: { label: '客户可以选择', sub: 'AI 会问客户想找哪位' },
        },
        bufferLabel: '预约间隔（分钟）',
        bufferHint: '每个预约之间的缓冲时间',
        maxDaysLabel: '最远可预约天数',
        maxDaysHint: '客户最多可以预约几天之后',
        weeklyHint: 'AI 只会在这些时段内帮客户预约。「容量」即是同一时间最多可以接几位客。',
        capacityLabel: '容量',
        capacitySuffix: '位客',
        addRange: '添加时段',
        blockedHint: '假期或特殊休息日 — 这些日子 AI 不会接受预约。',
        blockDatePlaceholder: '日期 (YYYY-MM-DD)',
        blockReasonPlaceholder: '原因（选填）',
        dayLabels: {
          sun: '周日',
          mon: '周一',
          tue: '周二',
          wed: '周三',
          thu: '周四',
          fri: '周五',
          sat: '周六',
        },
      },
      payment: {
        title: '付款方式',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        loadError: '加载失败',
        deleteConfirmTitle: '删除这个付款方式？',
        deleteConfirmBody: '删除后无法恢复。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '删除',
        nameEmptyTitle: '名称不可留空',
        nameEmptyBody: '第 {index} 个付款方式要有名称，例如「银行转账」',
        introHeading: '告诉客户如何付款',
        introBody: '添加几个付款方式，例如银行转账、PayPal、Wise。客户在网店结账或与 AI 下单时都会看到。',
        emptyTitle: '暂无付款方式',
        emptyHint: '点下面添加第一个',
        addBtn: '添加付款方式',
        cardIndexLabel: '第 {index} 个',
        statusActive: '已启用',
        statusInactive: '已停用',
        namePlaceholder: '名称，例如：银行转账',
        instructionsPlaceholder: '详情，例如：招商银行 123-456789 / 户名：ABC Studio',
      },
      stripeConnect: {
        title: '信用卡收款',
        introHeading: '让顾客用信用卡付款',
        introBody: '连结 Stripe 后，AI 帮顾客下单时会自动发送一个安全的付款链接 — 支持信用卡、Apple Pay、Google Pay。',
        statusNotConnected: '未连结',
        statusSetupIncomplete: '设置未完成',
        statusConnected: '已连结',
        statusChargesDisabled: '未启用收款',
        statusPayoutsDisabled: '提现审核中',
        badgeVerified: '已验证',
        badgePending: '待审核',
        connectBtn: '设置 Stripe',
        continueBtn: '继续完成设置',
        manageBtn: '更新 Stripe 资料',
        disconnectBtn: '断开 Stripe',
        disconnectConfirmTitle: '断开 Stripe？',
        disconnectConfirmBody: '断开后，顾客将无法使用信用卡付款，直到你重新连结。',
        disconnectConfirmCancel: '取消',
        disconnectConfirmAction: '断开',
        disconnectError: '当前无法断开连结',
        loadError: '无法加载 Stripe 状态',
        adminOnly: '仅店主（admin）可设置 Stripe。',
        sessionMissing: '请先登出再重新登录以刷新 session，然后重试。',
        feesNote: 'Stripe 会针对每笔交易收取自身手续费。NovixOne 不会另外加收任何费用。',
        webTitle: 'Stripe 设置',
        webClose: '关闭',
        webLoading: '正在加载 Stripe…',
        webError: '当前无法加载 Stripe，请检查网络后重试。',
        webRetry: '重试',
      },
      billing: {
        title: '订阅与用量',
        loadError: '暂时无法加载账单信息',
        currentPlanLabel: '你当前的套餐',
        usageLabel: '本月 AI 回复用量',
        unlimited: '无上限',
        enterpriseHint: '你的套餐没有用量上限。有问题请联系支持。',
        remainingLabel: '还有 {count} 次回复',
        dangerWarning: '你快用完本月的额度 — 用完后 AI 会暂停自动回复。考虑升级套餐或购买加购回复包。',
        warnWarning: '已使用超过八成，注意月底前的用量。',
        topupHeading: '加购回复包',
        topupWithBalance: '你还有 {count} 次加购回复，会在月结额度用完后自动扣。',
        topupWithoutBalance: '尚未购买加购回复包。加购次数可以跨月使用，用完月结额度会自动扣。可以在电脑版购买。',
        upgradeBtn: '升级套餐',
        manageBtn: '管理订阅',
        portalHint: '订阅管理、付款方式、发票与取消都在 Stripe 的安全页面处理。',
        openBrowserFailedTitle: '无法打开网页',
        openBrowserFailedBody: '请稍后再试。',
        plans: {
          free: { label: '免费', sub: '基本试用 — 足够开始' },
          starter: { label: '入门', sub: '单人工作室' },
          pro: { label: '专业', sub: '稳定生意流量' },
          business: { label: '商业', sub: '多客户高频次' },
          enterprise: { label: '企业', sub: '自定义用量' },
        },
      },
      apps: {
        title: '功能',
        intro: '开启你的店真正在做的事。关闭的功能会从菜单、AI 和网店消失 — 不会删除任何数据。',
        modulesHeading: '功能',
        focusHeading: '主力',
        focusHint: 'AI 主动推什么、首页先显示什么。只影响展示。',
        focusNone: '客服优先',
        focusNoneHint: 'AI 负责解答问题，不会主动推订单或预约。',
        readOnly: '只有所有者和经理可以更改功能。',
        lockedNote: '你当前的套餐不包含。请在 app.novixone.ai 管理套餐。',
        requiresOneOf: '需要先开启 {modules}。',
        requiresAll: '需要先开启 {modules}。',
        or: '或',
        notReadyOrders: '已开启，但还没有上架产品 — AI 还不能接单。',
        notReadyBookings: '已开启，但还没有可预约服务 — AI 还不能帮顾客预约。',
        confirmOffTitle: '关闭「{module}」？',
        confirmOffBody: '{impact}会保留并照常完成，但顾客和 AI 不能再新建。',
        impactOrders: '{count} 张未完成订单',
        impactBookings: '{count} 个未来预约',
        impactDeals: '{count} 个进行中商机',
        confirmOffCancel: '取消',
        confirmOffAction: '关闭',
        cascaded: '一并关闭：{modules}',
        saveError: '保存失败',
        loadError: '加载功能失败。',
        retry: '重试',
      },
      shop: {
        title: '商家设置',
        fallbackShop: '商家',
        businessTypeMissing: '尚未设置生意类型',
        allShopHint: '全店共用 · 更改会影响整个生意',
        pauseAlertTitle: '暂停 AI 自动回复？',
        pauseAlertBody: '新消息会直接等你亲自回复，暂停期间不会扣 AI quota。',
        pauseAlertCancel: '取消',
        pauseAlertConfirm: '暂停 AI',
        aiPausedTitle: 'AI 已暂停',
        aiPausedHint: '新消息会等你接管',
        aiActiveTitle: 'AI 自动回复',
        aiActiveHint: 'AI 会帮你自动处理对话',
        planHeading: '{plan}套餐',
        usageLoading: '加载中…',
        usageLoaded: '已用 {used} / {total} 次回复',
        unlimited: '无上限',
        usageWarnPercent: '用量已达 {percent}%',
        usageDangerPercent: '用量已达 {percent}% — 即将见底',
        sectionBusiness: '生意',
        sectionAI: 'AI 助手',
        sectionLocale: '地区与语言',
        rowHours: { title: '营业时间', sub: '每日开放时段' },
        rowPayment: { title: '付款方式', sub: '银行转账、PayPal、Wise 等' },
        rowStripe: { title: '信用卡收款', sub: '信用卡、Apple Pay、Google Pay（通过 Stripe）' },
        rowAvailability: { title: '可预约时段', sub: '每周开放时间、封锁日期' },
        rowPolicies: { title: '政策', sub: '取消规则、退款、休假模式' },
        rowStaff: { title: '服务人员', sub: '团队成员、服务指派' },
        rowCategories: { title: '商品分类', sub: '整理商品与服务的分类' },
        rowSoul: { title: 'AI 个性', sub: '语气、守则、红线' },
        rowOutreach: { title: '自动跟进', sub: '咨询未下单、老客户、下单后' },
        rowTemplates: { title: 'WhatsApp 消息模板', sub: '查看模板审核状态' },
        rowKnowledge: { title: '知识库', sub: 'FAQ、政策、常见回答' },
        rowAgents: { title: 'AI 代理', sub: '代理人格、技能、触发词' },
        rowRegion: '地区',
        rowTimezone: '时区',
        groups: {
          account: '账户',
          shop: '店铺',
          operations: '运营',
          messaging: '客户消息',
          advanced: '高级',
          upgrade: '升级可解锁',
        },
        rows: {
          account: { title: '店铺账户', sub: '店名、业务类型、简介' },
          business: { title: '店铺资料', sub: '地区、时区、语言' },
          apps: { title: '功能', sub: '订单、预约、商机 — 开或关' },
          operations: { title: '运营政策', sub: '自动确认、取消政策、假期模式' },
          scheduling: { title: '预约时段', sub: '每周时段、屏蔽日期、排班' },
          payment: { title: '收款方式', sub: 'Stripe、FPS、PayMe、转账、现金' },
          templates: { title: '消息模板', sub: 'WhatsApp 模板审批状态' },
          marketing: { title: '自动跟进', sub: '放弃兴趣、流失客户、下单后' },
          billing: { title: '套餐与账单', sub: '套餐、发票、AI credits' },
          data: { title: '数据与隐私', sub: '关闭账户' },
        },
        planSince: '{plan} 起',
        webOnlyHint: '团队、渠道、自定义字段和集成在网页后台管理。',
        rowLanguage: 'AI 回复语言',
        languageLabels: {
          yue: '广东话',
          zhHant: '繁體中文',
          zhHans: '简体中文',
          en: 'English',
        },
        planLabels: {
          free: '免费',
          starter: '入门',
          pro: '专业',
          business: '商业',
          enterprise: '企业',
        },
      },
    },
    languageScreen: {
      title: '语言',
      saveError: '保存失败',
      unknownError: '未知错误',
      heading: 'AI 与应用语言',
      hint: '控制 AI 回复客户的语言和应用界面语言。与地区无关 — 您可以在中国做生意但选择英文，适合服务海外客户。',
      languageLabels: {
        zhHant: { label: '繁體中文', sub: '港、台、海外华人通用' },
        zhHans: { label: '简体中文', sub: '中国大陆、新加坡通用' },
        en: { label: 'English', sub: 'International default' },
      },
    },
    shared: {
      loadFailed: '加载失败',
      retry: '重试',
    },
    voice: {
      recognitionFailed: '语音识别失败',
      permissionDenied: '需要麦克风与语音识别权限',
    },
    more: {
      title: '设置',
      fallbackUser: '用户',
      fallbackShop: '店铺',
      sectionPersonal: '个人设置',
      sectionShop: '店铺',
      profile: '个人资料',
      profileSub: '显示名称、登录邮箱',
      notifications: '通知偏好',
      notificationsSub: '哪些事件、哪个渠道提醒你',
      language: '应用程序语言',
      languageSub: '仅影响你设备上的界面，不影响 AI 回复',
      shopSettings: '店铺设置',
      shopSettingsSub: '{shop} · AI、营业时间、账单…',
      shopSettingsHint: '店铺设置会影响整个生意与所有同事',
      sectionData: '数据与隐私',
      closeAccount: '关闭账户',
      closeAccountSub: '停用店铺，宽限期后删除所有数据',
      signOut: '退出登录',
      support: '帮助与支持',
      supportSub: '联系 NovixOne 团队、查看您的请求',
    },
    helpDesk: {
      title: '支持',
      subtitle: '您向 NovixOne 支持团队提出的请求。',
      newRequest: '新请求',
      empty: '暂时没有支持请求',
      emptyHint: '联系支持后，对话会在这里显示。',
      loadError: '无法加载支持请求。',
      retry: '重试',
      category: '与什么有关？',
      categories: { billing: '账单及方案', whatsapp: 'WhatsApp', line: 'LINE', ai: 'AI 回复', bookings: '预约', orders: '订单及付款', account: '账户及登录', other: '其他' },
      subject: '主题',
      subjectPlaceholder: '例如：WhatsApp 客户收不到回复',
      description: '发生了什么事？',
      descriptionPlaceholder: '您做了什么、预期会怎样、实际发生了什么？',
      contact: '最方便的联系方式（选填）',
      contactPlaceholder: '例如：WhatsApp +852 …，或邮箱',
      attachedNote: '会随请求一并发送：应用版本、手机平台及您的角色，有助我们找出问题。',
      send: '发送请求',
      sending: '发送中…',
      sent: '已发送请求 {ref}，我们会在此处及通过邮件回复。',
      sendFailed: '请求未能发送，请再试一次。',
      subjectTooShort: '主题最少需要 3 个字。',
      descriptionTooShort: '请简单描述遇到的问题。',
      statuses: { open: '处理中', pending_merchant: '等待您回复', pending_internal: '工程团队跟进中', resolved: '已解决', closed: '已关闭' },
      waitingForYou: '支持已回复，等待您回应',
      updated: '更新于',
      you: '您',
      supportName: 'NovixOne 支持',
      replyPlaceholder: '输入回复…',
      replySend: '发送',
      replyFailed: '回复未能发送。',
      markSolved: '标记为已解决',
      markSolvedConfirm: '将此请求标记为已解决？',
      markSolvedBody: '7 天内仍可回复以重新开启。',
      cancel: '取消',
      solvedDone: '已标记为已解决。',
      closedNote: '此请求已关闭。如问题再次出现，请提出新请求。',
      resolvedNote: '此请求已解决。如仍有问题，请于 7 天内回复。',
      csatQuestion: '这次支持对您有帮助吗？',
      csatGood: '有帮助',
      csatBad: '没有帮助',
      csatThanks: '感谢您的意见。',
      slaNote: '支持团队于工作时间（周一至周五 09:00–18:00，香港时间）回复。',
      notFound: '找不到此支持请求。',
      announcementSeverity: { info: '通知', warning: '重要', critical: '紧急' },
      announcementKinds: { incident: '服务问题', maintenance: '系统维护', feature: '新功能', billing: '账单' },
      dismiss: '关闭',
      learnMore: '详情',
      update: '最新消息',
    },
    couponCheck: {
      title: '核对优惠券',
      subtitle: '客户出示优惠码时即时核对 — 不会占用名额。',
      inputPlaceholder: '输入优惠码，例：SUMMER100',
      checkBtn: '核对',
      checking: '核对中…',
      validTitle: '可以使用 ✓',
      invalidTitle: '不能使用',
      campaignLabel: '优惠活动',
      discountLabel: '折扣：{amount}',
      subtotalCaveat: '以 {amount} 订单估算 — 实际金额以结账时计算为准。',
      minSpendLine: '最低消费 {amount}',
      expiresLine: '{date} 截止',
      nextStepHint: '要核销：开单时输入这个码 — 折扣会自动套用并占用名额。',
      errorGeneric: '核对失败 — 请再试。',
    },
    business: {
      title: '业务',
      pillarOrders: '订单',
      pillarBookings: '预约',
      pillarInquiries: '咨询',
      pillarProducts: '商品',
    },
    businessSheets: {
      booking: {
        title: '建立预约',
        save: '保存',
        unknownError: '未知错误',
        fieldCustomer: '客户',
        fieldCustomerPlaceholder: '选择客户或添加',
        fieldService: '服务',
        fieldServicePlaceholder: '选择服务',
        usePackage: '使用套票「{name}」（剩 {left} 次）',
        usePackageHint: '扣一次套票，无需另行收费',
        serviceValue: '{duration} · {price}',
        fieldDate: '日期',
        fieldTime: '时间',
        fieldNotes: '备注',
        notesPlaceholder: '客户要求、特别安排…',
        hourLabel: '小时',
        minuteLabel: '分钟',
        pickServiceTitle: '选择服务',
        noServicesTitle: '暂无服务',
        noServicesHint: '到「商品」添加一个类型为「服务」的项目',
        serviceMinutes: '{minutes} 分钟',
        errors: {
          noCustomerTitle: '尚未选择客户',
          noCustomerBody: '请先选择一个客户',
          noServiceTitle: '尚未选择服务',
          noServiceBody: '请先选择一项服务',
          timeTitle: '时间错误',
          timeBody: '请选择有效的日期和时间',
          createFailedTitle: '建立失败',
          slotTitle: '这个时段无法预约',
          slotBody: '仍要创建预约吗？',
          bookAnyway: '仍要创建',
          cancel: '取消',
          resourceWarningTitle: '预约未分配房间',
          resourceWarningBody: '这个预约没有空闲的房间或仪器，请手动安排。',
        },
      },
      inquiry: {
        title: '建立咨询',
        save: '保存',
        unknownError: '未知错误',
        introTitle: '即时记录咨询',
        introBody: '接到的电话 / 客户上门说的话，记录下来方便跟进，不会漏掉。',
        fieldCustomer: '客户',
        fieldCustomerPlaceholder: '选择客户或添加',
        fieldType: '类型',
        fieldPriority: '优先',
        fieldSubject: '主题',
        fieldDescription: '详细内容',
        subjectPlaceholder: '一句话说明情况',
        descriptionPlaceholder: '客户说了什么、想要什么、预期如何跟进…',
        types: {
          inquiry: { label: '咨询', sub: '一般问题' },
          complaint: { label: '投诉', sub: '需要跟进' },
          feedback: { label: '意见', sub: '反馈 / 建议' },
          support: { label: '支援', sub: '技术 / 产品支援' },
        },
        priorities: {
          low: '低',
          normal: '普通',
          high: '高',
          urgent: '紧急',
        },
        errors: {
          noCustomerTitle: '尚未选择客户',
          noCustomerBody: '请先选择一个客户',
          noSubjectTitle: '需要主题',
          noSubjectBody: '请填写这次咨询的主题',
          noDescriptionTitle: '需要内容',
          noDescriptionBody: '请填写详细内容',
          createFailedTitle: '建立失败',
        },
      },
      product: {
        titleCreate: '添加商品',
        titleEdit: '编辑商品',
        typeProduct: '商品',
        typeService: '服务',
        fieldName: '名称',
        nameProductPlaceholder: '例如：手工皂',
        nameServicePlaceholder: '例如：60 分钟按摩',
        fieldPrice: '价格（{currency}）',
        pricePlaceholder: '0',
        fieldDuration: '时长（分钟）',
        durationPlaceholder: '例如：60',
        fieldDescription: '描述',
        descriptionPlaceholder: '客户在对话中会看到的介绍',
        fieldCategory: '分类',
        noCategory: '无分类',
        availableOn: '已上架',
        availableOnSub: '客户可以在对话中看到',
        availableOff: '已下架',
        availableOffSub: '暂时不会显示给客户',
        advanced: '高级设置',
        fieldOrderingMode: '下单方式',
        orderingDirect: '直接下单',
        orderingInquiry: '咨询报价',
        fieldPriceRange: '价格范围（选填）',
        priceMin: '最低',
        priceMax: '最高',
        fieldQuotationNotes: '报价备注',
        quotationPlaceholder: '例如：视款式与面积而定',
        fieldPaymentTiming: '付款时间',
        paymentFullUpfront: '全额预付',
        paymentDeposit: '订金',
        paymentOnCompletion: '完成后付',
        fieldDepositPercent: '订金百分比 (%)',
        depositPlaceholder: '例如：30',
        fieldFulfillment: '交付方式',
        fulfillmentDelivery: '送货',
        fulfillmentPickup: '自取',
        fulfillmentBoth: '送货或自取',
        fulfillmentNone: '不适用',
        fieldServiceDelivery: '服务地点',
        serviceInStore: '到店',
        serviceAtCustomer: '上门',
        serviceOnline: '线上',
        serviceHybrid: '弹性',
        fieldServiceLocation: '地址 / 地点名',
        serviceLocationPlaceholder: '例如：北京市朝阳区 XX 大厦 3 层',
        requiresBooking: '需要预约',
        requiresBookingHint: '开启后，客户在网店或与 AI 下单时都需要先选时段',
        saveEdit: '保存更改',
        saveCreate: '建立商品',
        createHint: '建立后可以在详情页添加图片',
        editOnWeb: '在桌面版开启以编辑进阶设定',
        errors: {
          nameTitle: '需要名称',
          nameBody: '商品名称是必填',
          priceTitle: '价格错误',
          priceBody: '请输入一个有效的价格',
          durationTitle: '时长错误',
          durationBody: '请输入一个有效的分钟数',
          saveFailedTitle: '保存失败',
          unknownError: '未知错误',
        },
      },
    },
    businessLists: {
      orders: {
        todayTotal: '今日 {amount}',
        filterAll: '全部',
        filterPending: '待处理',
        filterConfirmed: '已确认',
        filterPaid: '已付款',
        filterCompleted: '完成',
        filterCancelled: '取消',
        unpaid: '未付款',
        unknownCustomer: '未知客户',
        itemSummaryNone: '无项目',
        itemSummaryMore: '{first} 等 {total} 项 · 共 {totalQty} 件',
        empty: {
          allTitle: '暂无订单',
          allSub: '客户在 WhatsApp 下单后会出现在这里',
          pendingTitle: '没有待处理订单',
          pendingSub: '好好休息一下',
          confirmedTitle: '没有已确认订单',
          paidTitle: '没有已付款订单',
          completedTitle: '没有完成订单',
          cancelledTitle: '没有取消订单',
        },
      },
      bookings: {
        allBranches: '所有门店',
        filterToday: '今日',
        filterUpcoming: '未到',
        filterPending: '待确认',
        filterConfirmed: '已确认',
        filterCompleted: '完成',
        filterCancelled: '取消',
        statusPending: '待确认',
        statusConfirmed: '已确认',
        statusCompleted: '完成',
        statusCancelled: '取消',
        statusNoShow: '缺席',
        fallbackCustomer: '未知客户',
        fallbackService: '服务',
        cancelReasonPrefix: '取消原因：',
        dayCountLabel: '{count} 个预约',
        btnConfirm: '确认',
        btnComplete: '完成',
        btnCancel: '取消',
        btnCheckIn: '标记已到',
        btnStartService: '开始',
        btnEndService: '完成服务',
        attendanceArrived: '已到',
        attendanceInService: '服务中',
        attendanceLate: '迟到',
        attendanceDone: '服务完',
        depositDue: '订金未收',
        actionFailed: '操作失败',
        confirmFailed: '确认失败',
        completeFailed: '更新失败',
        cancelConfirmTitle: '取消预约？',
        cancelConfirmBody: '确定要取消这个预约吗？客户会自动收到通知。',
        cancelConfirmAction: '取消预约',
        cancelConfirmKeep: '不取消',
        cancelFailed: '取消失败',
        unknownError: '未知错误',
        create: '建立预约',
        createFirst: '建立第一个',
        empty: {
          upcomingTitle: '未来没有预约',
          upcomingSub: '客户通过网店、AI 预约或你自己添加都会出现在这里',
          pendingTitle: '没有待确认预约',
          confirmedTitle: '没有已确认预约',
          completedTitle: '没有完成预约',
          cancelledTitle: '没有取消预约',
          noShowTitle: '没有缺席记录',
        },
      },
      inquiries: {
        filterActive: '处理中',
        filterOpen: '新增',
        filterInProgress: '跟进中',
        filterResolved: '已解决',
        typeInquiry: '咨询',
        typeComplaint: '投诉',
        typeFeedback: '意见',
        typeSupport: '支援',
        priorityHigh: '高',
        priorityUrgent: '紧急',
        resolveConfirmTitle: '标记为已解决？',
        resolveConfirmBody: '确定这条咨询已经处理完毕？',
        resolveConfirmAction: '已解决',
        resolveConfirmCancel: '还没',
        updateFailed: '更新失败',
        unknownError: '未知错误',
        unknownCustomer: '未知客户',
        resolutionRecord: '处理记录',
        viewConversation: '查看对话',
        btnMarkInProgress: '跟进中',
        btnMarkResolved: '已解决',
        createBtn: '建立咨询',
        trackCase: '工单',
        trackDeal: '商机',
        filterOverdue: '逾期',
        filterMine: '我的',
        unassigned: '未指派',
        ownerLabel: '负责人：{name}',
        assignToMe: '指派给我',
        dueIn: '{time}内要回复',
        overdueBy: '逾期 {time}',
        stageLead: '新咨询',
        stageProposal: '提案中',
        stageNegotiation: '议价中',
        stageWon: '成交',
        stageLost: '流失',
        nextStep: '下一步：{text}',
        noValue: '未填金额',
        assignFailed: '指派失败',
        empty: {
          activeTitle: '没有待处理咨询',
          activeSub: '客户的支援与投诉会出现在这里，你也可以自己添加',
          openTitle: '没有新咨询',
          inProgressTitle: '没有跟进中的咨询',
          resolvedTitle: '没有已解决记录',
          closedTitle: '没有已关闭记录',
        },
      },
      products: {
        availableOfTotal: '{available} / {total} 上架中',
        searchPlaceholder: '搜索商品名称或分类',
        filterAll: '全部',
        filterProduct: '商品',
        filterService: '服务',
        tagService: '服务',
        tagPackage: '套票 · {count} 次',
        tagInquiryQuote: '咨询报价',
        tagMultiOption: '多选项',
        tagUnlisted: '下架',
        stateDraft: '草稿',
        stateArchived: '已归档',
        channelAiOnly: '仅限 AI',
        channelStoreOnly: '仅限网店',
        channelNone: '无通路',
        filterRestock: '要补货',
        waitingCount: '{count} 人在等',
        stockOut: '缺货',
        stockLow: '剩 {count} 件',
        stockOk: '库存 {count}',
        createBtn: '添加商品',
        addFirst: '添加第一件',
        empty: {
          noResultsTitle: '没有搜索结果',
          noResultsSub: '换个关键字试试',
          noProductsTitle: '暂无商品',
          noServicesTitle: '暂无服务',
          productCatalogSub: '添加商品或服务到 catalog，客户在对话中就会看到',
          restockTitle: '没有需要补货的商品',
          restockSub: '所有已设低库存线的商品库存均充足。',
        },
      },
    },
    customers: {
      title: '客户',
      countLabel: '{count} 位',
      searchPlaceholder: '搜索姓名、电话、邮箱',
      orderCountOne: '{count} 单',
      orderCountOther: '{count} 单',
      emptyNoResults: '无搜索结果',
      emptyNoSearchHint: '换个关键字试试',
      emptyNoCustomers: '暂无客户',
      emptyNoCustomersHint: '客户和你对话后会自动出现',
      lifecycle: {
        new: '新客',
        active: '活跃',
        atRisk: '流失风险',
        dormant: '沉睡',
        churned: '流失',
      },
    },
    conversations: {
      title: '对话',
      pendingCountLabel: '{count} 个待接管',
      filterInbox: '收件箱',
      filterPending: '待接管',
      filterMine: '我处理',
      filterAll: '全部',
      youPrefix: '你：',
      aiPrefix: 'AI：',
      noMessage: '无消息',
      unknownCustomer: '未知客户',
      humanActiveBadge: '人工处理中',
      draftBadge: 'AI 草稿待审',
      capturedKnowledgeHeader: '知识草稿',
      empty: {
        inboxTitle: '收件箱已清空',
        inboxSub: '所有客户对话都由 AI 处理中',
        pendingTitle: '没有对话等待接管',
        pendingSub: '好好休息一下',
        mineTitle: '你尚未接管任何对话',
        mineSub: '到「待接管」标签看看有没有新客户需要你',
        allTitle: '暂无任何对话',
        allSub: '客户消息会在这里出现',
      },
    },
    login: {
      heroTagline: 'WhatsApp AI 助手 · 为你的生意而设',
      feature1: 'AI 自动回复，支持多种语言',
      feature2: '订单、预约、客户管理一站式',
      feature3: '客户联系时即时通知',
      title: '登录',
      subtitle: '继续管理你的商家后台',
      googleSignIn: '使用 Google 登录',
      orEmail: '或使用邮箱',
      emailPlaceholder: 'your@email.com',
      sendLink: '发送登录链接',
      termsPrefix: '登录即表示你同意我们的',
      termsOfService: '《服务条款》',
      termsAnd: '与',
      privacyPolicy: '《隐私政策》',
      checkEmailTitle: '请检查你的邮箱',
      checkEmailSentTo: '我们已经把登录链接发送到 {email}',
      checkEmailHint: '点击链接就会自动返回 app。',
      tryDifferentEmail: '使用其他邮箱',
      loginFailed: '登录失败',
      googleLaunchFailed: '无法启动 Google 登录',
      googleNoAuth: '未收到 Google 授权',
    },
    updateRequired: {
      title: '需要更新',
      body: '此版本的应用已不再支持。请更新后继续管理您的店铺，您的数据不受影响。',
      cta: '立即更新',
    },
    needsOnboarding: {
      title: '请先在电脑完成设置',
      body: '账户已就绪，但需要先去 NovixOne 控制台完成商家设置，才能使用手机应用。大约一分钟即可完成。',
      ctaOpenWeb: '打开控制台',
      signOut: '登出',
    },
    loyaltySection: {
      heading: '会员',
      loadError: '无法加载会员资料',
      notMemberTitle: '未成为会员',
      notMemberHint: '客户于第一次赚分或兑换时会自动登记。',
      currentPoints: '当前积分',
      lifetimePoints: '累积积分',
      expiringWarning: '有 {count} 分将到期',
      expiringEarliest: '最早：',
      unnamedProgram: '未命名计划',
      typeStamp: '集印花',
      stampLabel: '印花',
      currentLabel: '当前',
      lifetimeLabel: '累积',
      referralCode: '推荐码',
      pendingHeading: '待核实兑换 ({count})',
      pendingPointsAndExpiry: '{points} 分 · 到期',
      ledgerHeading: '近期积分记录',
      balanceAfter: '余 {value}',
      topTierEnteredAt: '最高等级 · 进入时间 {date}',
      reasonLabels: {
        order_paid: '订单付款',
        order_completed: '订单完成',
        appointment_completed: '预约完成',
        signup_bonus: '注册奖赏',
        birthday_bonus: '生日礼',
        referral_earn: '推荐奖赏',
        tier_upgrade_bonus: '升级奖赏',
        stamp_earned: '集印花',
        stamp_complete_bonus: '集满印花',
        manual_adjustment: '手动调整',
        bonus: '额外奖赏',
        redemption_hold: '兑换预留',
        redemption_commit: '兑换完成',
        redemption_cancel: '兑换取消',
        redemption: '兑换',
        expiry: '积分过期',
      },
      statusLabels: {
        pending: '待核实',
        approved: '已批',
        used: '已使用',
        cancelled: '已取消',
        expired: '已过期',
      },
    },
    support: {
      analytics: {
        title: '趋势分析',
        periodWeek: '过去 7 天',
        periodMonth: '过去 30 天',
        empty: '暂无数据',
        labelRevenue: '营收',
        labelOrders: '订单',
        labelConversations: '对话',
        labelAiContainment: 'AI 自主处理',
        chartDailyRevenue: '每日营收',
        chartDailyConversations: '每日对话',
        chartMin: '最低 {value}',
        chartMax: '最高 {value}',
        vsPrev: '相较上期',
        flat: '与上期相同',
      },
      knowledgeList: {
        title: '知识库',
        loadError: '暂时无法加载知识库',
        searchPlaceholder: '搜索标题、内容或关键字',
        training: '部分知识仍在处理关键字…',
        emptySearchTitle: '找不到相关知识',
        emptyTitle: '知识库还是空的',
        emptySearchHint: '试试用其他关键字搜索。',
        emptyHint: '添加常见问题与政策，让 AI 自动回答客户。',
        addFirst: '添加第一条知识',
        categoryLabels: {
          faq: '常见问题',
          policy: '政策',
          info: '信息',
          general: '一般',
        },
        deactivated: '已停用',
        processing: '处理中',
      },
      knowledgeGaps: {
        title: '知识缺口',
        hint: '客人问过但答不上，意思相近的已归为一组。',
        emptyTitle: '暂时没有未解缺口',
        emptyHint: 'AI 答得上客人所有问题 — 或者还没开始接客。',
        loadError: '加载缺口失败',
        occurrences: (n: number) => `${n} 次`,
        customers: (n: number) => `${n} 位客人`,
        reopened: '答了但仍找不到',
        reopenedHint: '答案有，但客人再问时 AI 仍然找不到 — 去那条知识补上预期问法，不要再开一条。',
        samples: '客人原话',
        createEntry: '创建知识',
        openEntry: '打开知识',
        dismiss: '忽略',
        filterAll: '全部',
        filterMiss: '完全没覆盖',
        filterLowConfidence: '低信心',
        filterEscalation: 'AI 放弃',
        filterReopened: '答了但仍找不到',
      },
      knowledgeDrafts: {
        title: '待确认知识',
        hint: 'AI 从对话中抽取，你确认后才会生效。',
        emptyTitle: '没有待确认的内容',
        emptyHint: 'AI 抽到新知识时会出现在这里。',
        loadError: '加载待审知识失败',
        confidence: (pct: number) => `信心 ${pct}%`,
        approve: '确认',
        reject: '拒绝',
        approved: '已确认',
        rejected: '已拒绝',
        actionError: '操作失败',
      },
      knowledgeEditor: {
        titleNew: '添加知识',
        titleEdit: '编辑知识',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        deleteConfirmTitle: '删除知识？',
        deleteConfirmBody: '删除后 AI 不会再用这条知识回答客户。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '删除',
        deleteFailed: '删除失败',
        introHeading: '教 AI 如何回答客户',
        introBody: '标题用客户会问的方式写，内容是你想 AI 怎么回答。保存后 AI 会自动学着用来回答相关问题。',
        titleLabel: '标题',
        titlePlaceholder: '例如：你们几点开门？',
        titleHint: '用客户会问的方式写 — AI 会自动匹配类似的问法。',
        contentLabel: '内容',
        contentPlaceholder: '例如：我们早上十点到晚上七点，周日休息。法定假日会在 IG 公告。',
        categoryLabel: '分类',
        categoryLabels: {
          faq: '常见问题',
          policy: '政策',
          info: '信息',
          general: '一般',
        },
        activeLabel: '启用中',
        activeHint: '停用后 AI 不会再使用这条知识。',
        trainingNote: 'AI 还在学习这条知识的关键字…',
        delete: '删除知识',
      },
      soulEditor: {
        title: '语气与守则',
        hint: '本页设置应用于所有 AI 客服。系统基本规则（语言、不可捏造、确认后才下单）已自动生效。',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        unsavedTitle: '未保存的修改',
        unsavedBody: '离开会丢失未保存的守则修改。',
        unsavedStay: '留在本页',
        unsavedDiscard: '放弃修改',
        toneLabel: '语气风格',
        tones: {
          friendly: '友善轻松',
          professional: '专业正式',
          casual: '随意亲切',
          enthusiastic: '热情主动',
        },
        add: '加入',
        guidelinesLabel: '守则',
        guidelinesPlaceholder: '例：节日时主动提及优惠',
        guidelinesEmpty: '尚未设定 — 系统预设的基本规则会自动生效',
        guidelinesHint: '只需写你店铺独有的规矩。',
        redLinesLabel: '红线（绝对不可）',
        redLinesPlaceholder: '例：不要回答其他店铺的产品',
        redLinesEmpty: '尚未设定红线',
        redLinesHint: '写下 AI 绝对不可做的事。',
      },
      agentsList: {
        title: 'AI 代理',
        introHeading: 'AI 代理管理',
        introBody: '每个代理有自己的人格与技能。大部分商家只需要一个 — 进去修改人格描述并添加技能即可。',
        emptyTitle: '尚未建立任何代理',
        emptyBody: '通常系统会在 onboarding 自动帮你建立一个。如果没有，请联系支持。',
        active: '使用中',
        descriptionMissing: '未设描述',
        defaultBadge: '默认',
        inactive: '已停用',
        channelsNone: '未绑定渠道（默认路由）',
        soulLink: '语气与守则',
        soulLinkHint: '语气、守则、红线 — 所有 AI 客服共用',
      },
      agentsEditor: {
        titleFallback: '代理',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        notFound: '找不到代理',
        deactivateTitle: '停用代理？',
        deactivateBody: '停用后这个代理不会再处理任何对话。',
        deactivateCancel: '取消',
        deactivateAction: '停用',
        activeLabel: '使用中',
        inactiveLabel: '已停用',
        activeHint: '这个代理正在处理客户对话',
        inactiveHint: '停用的代理不会处理任何对话',
        nameLabel: '名称',
        namePlaceholder: '例如：主要助手',
        descLabel: '描述',
        descPlaceholder: '简短描述这个代理的角色',
        personaLabel: '人格提示词',
        personaPlaceholder: '用自然语言描述这个 AI 的性格、口吻与行为模式。这里的指令会加到系统 prompt 里。',
        personaHint: '与 AI 个性（Soul）分开 — Soul 是全店共用的语气与红线，这里是这个代理独有的指令。',
        skillsLabel: '技能',
        skillsAdd: '新增',
        skillsEmptyTitle: '暂无技能',
        skillsEmptyBody: '技能是特定场景的指令 — 例如「处理投诉」、「预约流程」。AI 会在检测到相关对话时自动启用。',
        skillLearningLabel: '学习触发词中',
        skillDeactivated: '已停用',
        sheetCreateTitle: '添加技能',
        sheetEditTitle: '编辑技能',
        skillNameLabel: '名称',
        skillNamePlaceholder: '例如：处理投诉',
        skillInstructionsLabel: '指令',
        skillInstructionsPlaceholder: '用自然语言描述 AI 在这个场景应该怎么做。例如：「客户投诉时，先道歉，然后询问具体情况…」',
        skillInstructionsHint: '触发词会自动由 AI 生成 — 你不用手动设置。',
        skillSaveCreate: '建立技能',
        skillSaveEdit: '保存更改',
        skillDelete: '删除技能',
        skillDeleteConfirmTitle: '删除技能？',
        skillDeleteConfirmBody: '删除「{name}」之后 AI 不会再使用这个技能。',
        skillDeleteConfirmCancel: '取消',
        skillDeleteConfirmAction: '删除',
        skillDeleteFailed: '删除失败',
        defaultBadge: '默认',
        defaultHint: '默认 AI 客服：未绑定渠道的对话都由它回答，不能停用。',
        setDefault: '设为默认',
        cannotDeactivateDefault: '默认 AI 客服不能停用，请先将另一个设为默认。',
        inactiveCannotBeDefault: '已停用的 AI 客服不能设为默认，请先启用。',
        skillGlobal: '全局',
        skillTriggerFailed: '触发词生成失败',
        skillTriggersLabel: '触发关键词',
        skillTriggersPlaceholder: '用逗号分隔，例：退款， complaint',
        skillTriggersHint: '留空由 AI 自动生成。英文需完整单词才算；中文只要包含即可。',
        skillActiveLabel: '启用技能',
        skillRegenerate: '重新生成触发词',
        skillRegenerateTitle: '重新生成触发词？',
        skillRegenerateBody: '会消耗 AI credits 根据指令重新生成，旧触发词会保留至新的完成。',
        skillNameTaken: '已有同名技能，请换个名字。',
        skillDeleteGlobalConfirmBody: '「{name}」是全局技能，所有 AI 客服都会使用。删除后无法恢复。',
        skillSaveFailed: '保存失败',
      },
      staffList: {
        title: '服务人员',
        introHeading: '服务人员管理',
        introBody: '加入你的团队成员，指定他们可以提供哪些服务。预约时会自动分派（不论是通过网店、AI 还是手动添加）。',
        emptyTitle: '尚未加入任何服务人员',
        emptyBody: '加入人员后，新预约可以自动分派，或者让客户在网店或对话中自己选。',
        allServices: '所有服务',
        someServices: '{count} 项服务',
        deactivated: '已停用',
      },
      staffEditor: {
        titleNew: '添加人员',
        titleEdit: '编辑人员',
        save: '保存',
        saveError: '保存失败',
        unknownError: '未知错误',
        deleteConfirmTitle: '删除人员？',
        deleteConfirmBody: '删除「{name}」— 若有未完成的预约会改为停用。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '删除',
        deleteFailed: '删除失败',
        nameLabel: '名称',
        namePlaceholder: '例如：Amy / 小美',
        phoneLabel: '电话',
        phonePlaceholder: '员工联系电话',
        emailLabel: '邮箱',
        colorLabel: '代表色',
        activeLabel: '使用中',
        inactiveLabel: '已停用',
        activeHint: '停用后不会再被分派预约',
        servicesLabel: '可提供的服务',
        servicesAll: '所有服务',
        servicesSome: '指定服务',
        pickCategoriesHint: '选分类 = 自动包含分类下所有服务',
        pickServicesHint: '或者逐项选择服务',
        noServicesHint: '暂无可指定的服务 — 先到商品添加服务类型的项目',
        delete: '删除人员',
      },
      assistant: {
        title: 'AI 助手',
        subtitle: '帮你管理商品、订单、预约、客户',
        voiceLocalePickerTitle: '语音识别语言',
        voiceLocaleAuto: '自动（跟随手机）',
        voiceLocaleCancel: '取消',
        voiceRecognitionFailed: '语音识别失败',
        voiceUnsupportedTitle: '不支持语音',
        voiceUnsupportedBody: '这台设备或这个 build 不支持语音识别。',
        voiceStartFailed: '语音启动失败',
        voiceListeningChip: '聆听中…请说',
        voiceListeningPlaceholder: '聆听中…',
        noPermissionTitle: '没有权限',
        noCameraPermission: '需要相机权限才能使用',
        noPhotoPermission: '需要照片权限才能使用',
        iCloudTitle: '照片还在 iCloud',
        iCloudBody: '这张照片还没下载到手机，请打开 iCloud 等下载完再选择。',
        imageUploadFailed: '图片上传失败',
        attachSheetTitle: '添加图片',
        attachCamera: '拍照',
        attachLibrary: '从相册选',
        attachCancel: '取消',
        thinking: 'AI 助手思考中…',
        sendFailed: '发送失败',
        uploadingImage: '上传图片中…',
        clearTitle: '清空对话？',
        clearBody: '清空后 AI 不会记得之前的对话。',
        clearCancel: '取消',
        clearAction: '清空',
        clearFailed: '清空失败',
        inputPlaceholder: '请 AI 助手帮你…',
        imageFallbackPrompt: '请看这张图片',
        emptyTitle: 'AI 助手',
        emptyBody: '你的 24/7 AI 员工\n有什么想做或想知道，直接问我',
        emptySuggestions: {
          followUp: '今天有没有客户需要跟进？',
          addProduct: '拍张照片添加新产品（按下面 📷）',
          tomorrowBookings: '明天有多少预约？',
          lapsedCustomers: '哪些客户一个月没回来？',
        },
        imageCountBadge: '{count} 张图片',
        localeLabels: {
          zhHK: '廣東話',
          zhTW: '繁體中文（台灣）',
          zhCN: '簡體中文',
          enUS: 'English (US)',
          enGB: 'English (UK)',
          enAU: 'English (Australia)',
          jaJP: '日本語',
          koKR: '한국어',
          thTH: 'ภาษาไทย',
          viVN: 'Tiếng Việt',
        },
      },
    },
    components: {
      phoneInput: {
        placeholder: '电话号码',
      },
      countryPicker: {
        title: '选择国家 / 地区',
        done: '完成',
        searchPlaceholder: '搜索国家 / 地区码',
        emptyResults: '没有搜索结果',
      },
      topupSheet: {
        openPaymentFailed: '无法打开付款',
        unknownError: '未知错误',
        title: '加购回复',
        subtitle: '一次付款，回复跨月都能使用',
        recommended: '推荐',
        replyCountLabel: '{count} 次 AI 回复',
        perReply: '{price}/次',
        footer: '使用 Stripe 付款，付完会自动加入你的账户',
        bundleSmall: '小量',
        bundleMedium: '中量',
        bundleLarge: '大量',
      },
      customerEditSheet: {
        title: '编辑客户',
        saveError: '保存失败',
        unknownError: '未知错误',
        fieldName: '名称',
        fieldNamePlaceholder: '客户名',
        fieldPhone: '电话',
        fieldEmail: '邮箱',
        fieldTags: '标签',
        tagInputPlaceholder: '输入标签后按回车',
        save: '保存更改',
      },
      customerPicker: {
        titleDefault: '选择客户',
        addNew: '新客户',
        searchPlaceholder: '搜索姓名或电话',
        emptyNoResults: '没有搜索结果',
        emptyNoCustomers: '暂无客户',
        addNewHint: '点右上「新客户」添加',
        newCustomerTitle: '新客户',
        save: '保存',
        nameRequiredTitle: '需要名称',
        nameRequiredBody: '客户名称为必填',
        phoneInvalidTitle: '电话格式错误',
        phoneInvalidBody: '请检查国家码与本地号码的数字长度。',
        createFailedTitle: '建立失败',
        nameLabel: '姓名',
        namePlaceholder: '客户的称呼',
        phoneLabel: '电话',
        phoneHint: '有电话的话，客户之后用 WhatsApp 联系你会自动对应',
      },
      notesSection: {
        title: '备注',
        composerPlaceholder: '写下关于这位客户的备注…',
        visibleToAi: 'AI 可见',
        hiddenFromAi: 'AI 不可见',
        addBtn: '新增',
        loadError: '加载备注失败',
        emptyLabel: '暂无备注',
        deleteConfirmTitle: '删除备注？',
        deleteConfirmBody: '删除后无法恢复。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '删除',
        createFailedTitle: '新增失败',
        deleteFailedTitle: '删除失败',
        unknownError: '未知错误',
      },
      memoriesSection: {
        title: 'AI 记忆 · {count}',
        titleNoCount: 'AI 记忆',
        addBtn: '新增',
        cancelBtn: '取消',
        keyPlaceholder: '标识，例如：付款方式',
        valuePlaceholder: '内容，例如：支付宝',
        saveBtn: '保存记忆',
        loadError: '加载记忆失败',
        emptyLabel: '暂无 AI 记忆 — AI 对话后会自动提取',
        deleteConfirmTitle: '删除记忆？',
        deleteConfirmBody: '「{key}」的记忆会被移除。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '删除',
        createFailedTitle: '新增失败',
        deleteFailedTitle: '删除失败',
        unknownError: '未知错误',
        categoryLabels: {
          preference: '偏好',
          behavior: '行为',
          personal: '个人',
          complaint: '投诉',
        },
        sourceManual: '手动',
        sourceAi: 'AI 提取',
        confidenceLabel: '信心 {percent}%',
        mentionCount: '提及 {count} 次',
      },
    },
    detail: {
      customer: {
        titleFallback: '客户',
        loadError: '客户加载失败',
        back: '返回',
        statTotalSpend: '总消费',
        statTotalOrders: '总订单',
        statTotalOrdersSuffix: '单',
        statEngagement: '互动分数',
        statHealth: '健康分数',
        sectionContact: '联系信息',
        sectionTags: '标签',
        sectionActivity: '活动',
        sectionTimeline: '时间线 · {count}',
        contactPhone: '电话',
        contactEmail: '邮箱',
        notProvided: '未提供',
        lastSeen: '上次联系',
        lastOrder: '上次下单',
        summaryFallback: '（暂无摘要）',
        messageCount: '{count} 条消息',
        orderStatus: {
          pending: '待处理',
          confirmed: '已确认',
          paid: '已付款',
          completed: '完成',
          cancelled: '取消',
        },
        conversationStatus: {
          aiActive: 'AI 处理中',
          pendingHandoff: '待接管',
          humanActive: '人工中',
          resolved: '已完成',
        },
        unpaid: '未付款',
        lifecycle: {
          new: '新客',
          active: '活跃',
          atRisk: '流失风险',
          dormant: '沉睡',
          churned: '流失',
        },
        inquiryTypes: {
          inquiry: '咨询',
          complaint: '投诉',
          feedback: '反馈',
          support: '支援',
        },
        inquiryStatus: {
          open: '待处理',
          inProgress: '处理中',
          resolved: '已解决',
          closed: '已关闭',
        },
        inquiryPriority: {
          low: '低',
          normal: '普通',
          high: '高',
          urgent: '紧急',
        },
      },
      conversation: {
        approval: {
          title: '待批准',
          subtitle: 'AI 想在你的系统执行以下操作，需要同事批准：',
          approve: '批准',
          reject: '拒绝',
          approveConfirmTitle: '批准这个操作？',
          approveConfirmBody: '会立即在你的系统执行，并通知客户结果。',
          rejectConfirmTitle: '拒绝这个操作？',
          rejectConfirmBody: '不会执行；客户会收到「同事未能批准」。',
          noPermission: '你没有批准此类操作的权限',
          expires: '{time} 前有效',
          done: '已执行 ✅',
          failed: '执行失败 — 已转交同事跟进',
          rejected: '已拒绝',
          alreadyDecided: '已经有同事处理了',
          expired: '已过期',
        },
        externalTools: {
          ranInYourSystem: 'AI 在你的系统执行',
          args: '参数',
          result: '结果',
          noFacts: '除了状态之外没有其他资料返回。',
          close: '关闭',
          states: {
            read: '已查询',
            done: '已完成',
            awaiting_customer: '待客户确认',
            awaiting_approval: '待同事批准',
            unavailable: '无法连接',
            blocked: '未执行',
          },
        },
        customerFallback: '客户',
        loadError: '对话加载失败',
        back: '返回',
        unknownError: '未知错误',
        takeoverClaimedTitle: '已被接管',
        takeoverClaimedBody: '另一位同事已经接手这个对话。',
        takeoverClaimedOk: '了解',
        takeoverFailed: '接管失败',
        returnToAiFailed: '操作失败',
        sendFailed: '发送失败',
        draftFailed: 'AI 起草失败',
        draftBtnIdle: 'AI 起草回复',
        pendingDraftTitle: 'AI 已拟好回复 · 等你审批',
        pendingDraftSend: '发送',
        pendingDraftDiscard: '放弃',
        pendingDraftHint: '如有需要可在此修改。发送后不会接管对话，AI 会继续拟稿。',
        draftBtnLoading: 'AI 思考中…',
        inputPlaceholder: '输入回复...',
        returnToAi: '交回 AI 跟进',
        takeoverBtn: '接管对话',
        claimBtn: '主动接管',
        conversationResolved: '对话已完成',
        emptyMessages: '暂无消息',
        episodeStarted: '新一段对话',
        episodePrevious: '上一段：{reason} · {handled}',
        episodeCloseReasons: { settled: '已完成（自动）', staff: '同事已结束', ai: 'AI 已结束', api: '系统已结束', idle: '闲置结束', merged: '旧对话' },
        episodeHandledBy: { ai_only: 'AI 处理', handed_off: '转给同事', human: '同事处理' },
        aiAssistantLabel: 'AI 助手',
        fromWhatsAppApp: '经 WhatsApp Business App',
        importedFromWhatsAppApp: '由 WhatsApp Business App 导入',
        rateUp: '这个回复好',
        rateDown: '这个回复有问题',
        rateWithdraw: '取消评分',
        rateReasonTitle: '有什么问题？',
        rateReasons: {
          wrong_info: '资料错误',
          wrong_tone: '语气不对',
          should_escalate: '应该转真人',
          hallucination: '编造 / 乱说',
          other: '其他',
        },
        rateFailed: '评分失败',
        mediaAudio: '语音消息',
        mediaDocument: '文件附件',
        mediaTapOpen: '点击打开',
        insightsTitle: 'AI 洞察',
        insightsUrgentBadge: '有急需跟进',
        insightsCountBadge: '{count} 项跟进',
        insightsUnhappyBadge: '客户不满',
        objectionsLabel: '客户疑虑',
        actionsSuggestionLabel: '建议跟进',
        actionComplete: '标记完成',
        actionDismiss: '略过',
        purchaseIntent: '购买意向 {percent}%',
        urgencyHigh: '高紧急度',
        urgencyMedium: '中紧急度',
        sentiment: {
          veryNegative: '非常负面',
          negative: '负面',
          neutral: '中性',
          positive: '正面',
          veryPositive: '非常正面',
        },
        priority: {
          low: '低',
          medium: '中',
          high: '高',
          urgent: '急',
        },
        statusPill: {
          pendingHandoff: '待接管',
          humanActive: '人工',
          aiActive: 'AI',
          resolved: '完成',
        },
        statusLabel: {
          aiActive: 'AI 处理中',
          pendingHandoff: '等候负责人接管',
          humanActive: '人工处理中',
          resolved: '已完成',
        },
        attachImage: '附加图片',
        imageReady: '图片已准备好',
        imagePermDeniedTitle: '需要相册权限',
        imagePermDeniedBody: '请在手机设置里允许 NovixOne 访问相册',
        imageTooLargeTitle: '图片太大',
        imageTooLargeBody: '请选择 8 MB 以下的图片',
        resolveBtn: '结束对话',
        resolveConfirmTitle: '结束对话？',
        resolveConfirmBody: '结束后对话会移至已解决列表，之后可重新打开。',
        resolveConfirmOk: '结束',
        resolveFailed: '结束失败',
        cancel: '取消',
        moreActions: '更多',
        actionTransfer: '🔁 转交同事',
        actionSnooze: '⏰ 暂缓处理',
        actionUnsnooze: '🔔 取消暂缓',
        snoozeTitle: '暂缓这个对话',
        snoozeBody: '暂缓中会从默认列表收起',
        snooze1h: '1 小时',
        snooze4h: '4 小时',
        snoozeTomorrow: '明早 9 点',
        snoozeFailed: '暂缓失败',
        transferComingSoonTitle: '同事选择即将推出',
        transferComingSoonBody: '暂请在电脑版进行转交',
      },
      product: {
        titleFallback: '商品',
        loadError: '商品加载失败',
        back: '返回',
        unknownError: '未知错误',
        deleteConfirmTitle: '下架这件商品？',
        deleteConfirmBody: '下架后客户不会再看到，你随时可以在编辑中重新上架。',
        deleteConfirmCancel: '取消',
        deleteConfirmAction: '下架',
        deleteFailedTitle: '失败',
        noPermissionTitle: '没有权限',
        noCameraPermission: '需要相机权限才能使用',
        noPhotoPermission: '需要照片权限才能使用',
        iCloudTitle: '照片还在 iCloud',
        iCloudBody: '这张照片未下载到手机，请打开 iCloud 等下载完再选择。',
        uploadFailedTitle: '上传失败',
        imageActionSheet: {
          camera: '拍照',
          library: '相册',
          cancel: '取消',
          addImageTitle: '添加照片',
        },
        deleteImageTitle: '删除这张照片？',
        deleteImageBody: '删除后无法恢复。',
        genericFailed: '失败',
        uploading: '上传中…',
        addFirstImage: '添加第一张照片',
        tagService: '服务',
        tagPackage: '套票 · {count} 次',
        tagInquiryQuote: '咨询报价',
        tagMultiOption: '多选项',
        tagUnlisted: '下架',
        stateDraft: '草稿',
        stateArchived: '已归档',
        channelAiOnly: '仅限 AI',
        channelStoreOnly: '仅限网店',
        channelNone: '无通路',
        perfTitle: '过去 30 天',
        perfUnits: '卖出',
        perfRevenue: '营业额',
        perfWaiting: '等待到货',
        perfNoSales: '过去 30 天没有卖出过。',
        perfLastSold: '最后卖出：{date}',
        aiPreviewTitle: 'AI 看到什么',
        aiPreviewShow: '看 AI 收到的内容',
        aiPreviewHide: '收起',
        aiPreviewUnreachable: 'AI 目前无法访问此商品 —— 并非内容问题，而是状态或渠道设置。',
        aiPreviewReadiness: '就绪 {ready}/{of}',
        openOnDesktop: '在电脑继续编辑',
        openOnDesktopHint: '完整编辑（规格、修饰项、排程价）在网页版',
        durationMinutes: '{minutes} 分钟',
        sectionDescription: '描述',
        sectionVariants: '选项',
        variantsEmpty: '暂无选项 — 例如不同 size、颜色、时长',
        addVariant: '添加选项',
        stockOut: '缺货',
        stockLow: '剩 {count} 件',
        stockOk: '库存 {count}',
        stockRemaining: '剩 {count}',
        stockTotal: '库存 {count}',
        variantAvailable: '上架',
        variantUnavailable: '下架',
        variantSheet: {
          createTitle: '添加选项',
          editTitle: '编辑选项',
          nameLabel: '名称',
          namePlaceholder: '例如：大码 / 60 分钟 / 红色',
          priceLabel: '价格（留空 = 跟随主商品）',
          pricePlaceholder: '0',
          stockLabel: '库存数量',
          stockPlaceholder: '留空 = 不追踪库存',
          available: '上架中',
          unavailable: '已下架',
          saveCreate: '建立选项',
          saveEdit: '保存更改',
          delete: '删除选项',
          deleteConfirmTitle: '删除选项？',
          deleteConfirmBody: '删除「{name}」后无法恢复。',
          deleteConfirmCancel: '取消',
          deleteConfirmAction: '删除',
          deleteFailed: '删除失败',
          createFailed: '建立失败',
          saveFailed: '保存失败',
          unknownError: '未知错误',
        },
      },
      order: {
        titleFallback: '订单',
        loadError: '订单加载失败',
        back: '返回',
        unknownError: '未知错误',
        unknownCustomer: '未知客户',
        confirmFailed: '确认失败',
        updateFailed: '更新失败',
        cancelFailed: '取消失败',
        markPaidTitle: '确认收款？',
        markPaidBody: '标记为「已付款」之后，客户会收到确认通知。',
        markPaidCancel: '取消',
        markPaidConfirm: '已收款',
        cancelOrderTitle: '取消订单？',
        cancelOrderBody: '取消后无法恢复，客户会自动收到通知。',
        cancelOrderCancel: '不取消',
        cancelOrderAction: '取消订单',
        actionConfirm: '确认订单',
        actionMarkPaid: '确认收款',
        actionShip: '标记已发货',
        actionComplete: '完成',
        actionCancel: '取消订单',
        sectionItems: '订单项目',
        itemCount: '{count}',
        total: '总计',
        collectedLabel: '已收',
        outstandingLabel: '未收',
        overpaidLabel: '多收（待退）',
        revisionLabel: '已修改 {count} 次',
        sectionOther: '其他信息',
        paymentMethodLabel: '付款方式',
        notesLabel: '备注',
        status: {
          pending: '待处理',
          confirmed: '已确认',
          paid: '已付款',
          completed: '完成',
          cancelled: '取消',
        },
        unpaid: '未付款',
      },
    },
    home: {
      welcome: {
        greetingNight: '深夜',
        greetingMorning: '早安',
        greetingAfternoon: '下午好',
        greetingEvening: '晚上好',
        fallbackName: '老板',
        tagline: '今天和 NovixOne 一起工作',
      },
      aiActions: {
        sectionLabel: 'AI 建议跟进',
        itemCountLabel: '{count} 项',
        empty: '暂时没有建议要跟进，AI 会在对话后自动挑出最值得你处理的客户',
        types: {
          followUp: '跟进',
          winBack: '挽回',
          crossSell: '追加销售',
          resolveIssue: '处理问题',
          nurture: '培养关系',
          thankVip: '感谢 VIP',
          checkSatisfaction: '确认满意度',
        },
        priority: {
          low: '低',
          medium: '中',
          high: '高',
          urgent: '急',
        },
      },
      attention: {
        atRisk: '流失风险',
        highIntent: '高意向',
        openInquiries: '待处理咨询',
      },
      bookings: {
        sectionLabel: '今日预约',
        viewAll: '查看全部',
        statusPending: '待确认',
        statusConfirmed: '已确认',
        fallbackCustomer: '未知客户',
        fallbackService: '服务',
      },
      stats: {
        sectionLabel: '今日概况',
        revenue: '今日营业额',
        orders: '今日订单',
        conversations: '今日对话',
        quotaUsed: '本月用量',
        trendsTitle: '趋势分析',
        trendsSub: '过去 7 天 / 30 天的营收、对话与订单',
        couponCheckTitle: '核对优惠券',
        couponCheckSub: '客户在门店出示优惠码时即时核对',
      },
      activeConvs: {
        countLabel: '{count} 个进行中对话',
        viewAll: '查看全部对话 →',
      },
      followUps: {
        sectionLabel: '今日跟进',
        onRecord: '跟进',
        noneOnRecord: '这张单没有待办跟进。',
        viewAll: '查看全部',
        mine: '我的',
        overdue: '{count} 项逾期',
        empty: '今日没有待跟进',
        noDate: '无日期',
        done: '完成',
        markDone: '标记完成',
        laterAction: '稍后跟进',
        created: '已安排跟进',
        outcomePrompt: '结果如何？',
        outcomes: {
          done: '完成',
          no_answer: '联系不上',
          rescheduled: '改期',
          not_needed: '不需要',
        },
        quick: {
          hour: '1 小时后',
          tonight: '今晚',
          tomorrow: '明天',
          nextWeek: '下周一',
        },
      },
      pending: {
        sectionLabel: '待接管对话',
        viewAll: '查看全部',
        noMessage: '无消息',
        unknownCustomer: '未知客户',
      },
      quota: {
        sectionLabel: 'AI 回复用量',
        thisMonth: '本月回复',
        topupRemaining: '另外有 {count} 次加购回复',
        topup: '加购回复',
        topupWarn: '快用完 — 加购回复',
      },
      fab: {
        askAssistant: '询问助手',
      },
      channelDown: {
        title: 'WhatsApp {number} 已中断',
        body: 'AI 目前不会回复这个号码的客人。请在网页版「设置 › 通讯渠道」重新连接。',
      },
      aiPause: {
        title: 'AI 已暂停',
        hint: '新消息会直接等你接管',
        hintWithDuration: '已暂停 {duration} · 新消息会直接等你接管',
        lessThanMinute: '不到 1 分钟',
        resume: '恢复 AI',
      },
    },
    bookingAdvanced: {
      cancel: '取消',
      save: '保存',
      keep: '保留',
      unknownError: '出了点问题，请再试一次。',
      failedTitle: '操作未完成',
      reasonLabel: '原因（必填）',
      reasonPlaceholder: '例如：补偿一次、预约出错',
      waitlist: {
        title: '候补名单',
        openButton: '候补 · {count}',
        filterOpen: '等待中',
        filterActive: '未通知',
        filterNotified: '已通知',
        filterConverted: '已预约',
        filterExpired: '已过期',
        filterCancelled: '已关闭',
        filterAll: '全部',
        allServices: '全部服务',
        empty: '暂时没有人候补',
        emptyHint: '客人找不到合适时段时加入候补，有空位就可以马上通知。',
        dateWindow: '{from} 至 {to}',
        anyTime: '任何时间',
        timeWindow: '{from}–{to} 开始',
        timeFromOnly: '{from} 之后开始',
        timeToOnly: '{to} 之前开始',
        everyDay: '任何日子',
        notifiedTimes: '已通知 {count} 次',
        anyStaff: '任何员工',
        status: {
          active: '等待中',
          notified: '已通知',
          converted: '已预约',
          expired: '已过期',
          cancelled: '已关闭',
        },
        add: '新增',
        addTitle: '加入候补名单',
        customer: '客人',
        customerPlaceholder: '选择客人',
        service: '服务',
        servicePlaceholder: '选择服务',
        staff: '员工',
        branch: '门店',
        dateFrom: '从',
        dateTo: '至',
        earliestStart: '最早开始',
        latestStart: '最晚开始',
        weekdays: '可接受的星期',
        weekdayShort: ['日', '一', '二', '三', '四', '五', '六'],
        note: '备注',
        notePlaceholder: '例如：想要靠窗位置',
        submit: '加入候补',
        added: '已加入候补名单',
        alreadyOn: '这位客人已经在这个候补名单里',
        prefsUpdated: '已更新这位客人的候补时段',
        closeEntry: '关闭',
        closeConfirmTitle: '关闭这个候补？',
        closeConfirmBody: '记录会保留，但之后有空位也不会再匹配这位客人。',
        remove: '移除',
        removeConfirmTitle: '移除这个候补？',
        removeConfirmBody: '这个候补会被永久删除。',
        notify: '通知',
        notifyTitle: '通知 {name} 有空位',
        notifyHint: '不会为客人保留位置，客人需要自行预约。发送前会再次检查时段是否空闲。',
        slotDate: '日期',
        slotTime: '开始时间',
        notifySend: '发送',
        notified: '已通知',
        matchesTitle: '{count} 位客人在等这个时段',
        errors: {
          SLOT_TAKEN: '这个时段已经没有空位',
          SLOT_PAST: '这个时段已经过了',
          OPTED_OUT: '这位客人不接收推广消息',
          ALREADY_NOTIFIED: '已经通知过这位客人这个时段',
          ENTRY_CLOSED: '这个候补已经结束',
          NOT_A_MATCH: '这个时段不是客人要的服务或门店',
          NOT_SENT: '无法发送（没有可用渠道或模板未通过审核）',
          SEND_FAILED: '发送失败，请重试',
          SLOT_NOT_FOUND: '找不到这个时段的服务、门店或员工',
          APPOINTMENT_NOT_FOUND: '这个预约已经不存在',
          INVALID_RANGE: '日期范围无效',
          INVALID_TIME: '时间无效',
          INVALID_WEEKDAYS: '至少选择一天',
          INVALID_DATE: '日期无效',
          NOT_A_SERVICE: '只有可预约的服务才能候补',
          LOCATION_NOT_FOUND: '找不到这家门店',
          LOCATION_REQUIRED: '请选择门店',
          MODULE_DISABLED: '高级预约功能未开启',
          NOT_FOUND: '这个候补已经不存在',
          FORBIDDEN: '你没有权限执行此操作',
        },
      },
      packages: {
        sectionTitle: '套餐（{count}）',
        left: '剩 {left} 次',
        usage: '已用 {used} · 已预约 {reserved} · 共 {total} 次',
        expires: '有效期至 {date}',
        noExpiry: '无到期日',
        target: '适用：{service}',
        status: {
          active: '有效',
          completed: '已用完',
          cancelled: '已取消',
          expired: '已过期',
        },
        cancelReason: {
          staff: '员工取消',
          order_refunded: '订单已退款',
          order_cancelled: '订单已取消',
          payment_voided: '付款已作废',
        },
        screenTitle: '套餐',
        bookingsTitle: '使用这个套餐的预约',
        noBookings: '还没有预约使用过这个套餐',
        manage: '调整',
        sessions: '次数',
        sessionsHelp: '+ 为退回次数给客人，− 为扣减次数',
        newExpiry: '新到期日：{date}',
        extendBy: '+{days} 天',
        keepExpiry: '不变',
        apply: '保存调整',
        cancelPackage: '取消套餐',
        cancelConfirmTitle: '取消这个套餐？',
        cancelConfirmBody: '之后不能再用来预约。已有的 {reserved} 个未来预约会保留各自的次数。',
        reactivate: '重新启用',
        reviveConfirmTitle: '重新启用已退款的套餐？',
        reviveConfirmBody: '这个套餐因为订单退款、取消或付款作废而被取消。重新启用等于再次送出这些次数。',
        reviveConfirm: '仍然重新启用',
        reasonRequired: '请先填写原因',
        saved: '套餐已更新',
        errors: {
          OVER_TOTAL: '剩余次数不足，未来预约已经占用了一部分',
          NEGATIVE: '已用次数不能少于 0',
          BELOW_SPENT: '这些次数已被已完成的预约使用，请撤销那些预约',
          EXPIRY_BEFORE_BOOKING: '有未来预约在新到期日之后使用这个套餐',
          EXPIRY_PASSED: '套餐已过期，需要延长有效期才能重新启用',
          REVIVE_NEEDS_CONFIRM: '这个套餐的订单已退款或取消，需要确认才能重新启用',
          NOT_FOUND: '这个套餐已经不存在',
          REASON_REQUIRED: '请先填写原因',
          NOTHING_TO_CHANGE: '没有需要修改的内容',
          BAD_EXPIRY: '新到期日无效',
          FORBIDDEN: '只有可以退款的同事才能调整套餐',
        },
      },
      booking: {
        title: '预约',
        loadError: '无法加载这个预约',
        back: '返回',
        customer: '客人',
        service: '服务',
        when: '时间',
        staff: '员工',
        branch: '门店',
        amount: '金额',
        packagePaid: '套餐 · {n}/{m}',
        packagePaidShort: '套餐',
        deposit: '订金',
        depositDue: '未收订金',
        depositPaid: '已收订金',
        order: '订单',
        seriesLabel: '循环预约 第 {i}/{t} 次 · {frequency}',
        seriesFrequency: {
          weekly: '每周',
          biweekly: '每两周',
          triweekly: '每三周',
          monthly: '每月',
        },
        seriesStatus: {
          active: '进行中',
          completed: '已完成',
          cancelled: '已取消',
        },
        seriesSessions: '所有次数',
        seriesThis: '本预约',
        cancelRemaining: '取消剩余次数',
        cancelRemainingConfirm: '取消剩余 {count} 次？已完成和已过去的次数会保留，相关订单一并取消。',
        cancelRemainingDone: '已取消 {count} 次',
        ordersHeld: '以下订单已收款，请在订单中退款：{orders}',
        openWaitlist: '候补名单',
        errors: {
          SERIES_ENDED: '这个循环预约已经结束',
          NOTHING_TO_CANCEL: '没有仍然有效的未来次数可以取消',
          FORBIDDEN: '你没有权限执行此操作',
          NOT_FOUND: '这个预约已经不存在',
        },
      },
    },
  },

  loyalty: {
    title: '忠诚度计划',
    subtitle: '奖励客户，提升复购率',

    filters: {
      member: '会员',
      topTier: 'VIP 级',
      highValueNonMember: '高消费未入会',
    },
    sort: {
      lifetimePoints: '累计积分',
    },

    tier: {
      label: '会员等级',
      notMember: '尚未成为会员',
      nextTier: '下一级：{name}',
      pointsToNext: '距离 {name} 还差 {count} 分',
      benefits: '会员权益',
      rankFallback: '第 {rank} 级',
    },

    points: {
      balance: '可用积分',
      lifetime: '累计积分',
      earned: '+{count} 分',
      redeemed: '−{count} 分',
      expiringSoon: '{count} 分将于 {date} 到期',
      label: '积分',
      labelShort: '分',
    },

    member: {
      enrolledSince: '自 {date} 加入',
      pending: '待处理',
      paused: '已暂停',
      expired: '已过期',
      banned: '已停权',
    },

    program: {
      title: '计划',
      create: '创建计划',
      edit: '编辑计划',
      active: '使用中',
      inactive: '未启用',
      autoEnroll: {
        label: '自动加入会员',
        description: '客户首次符合条件的消费将自动成为会员。',
      },
    },

    backfill: {
      stepTitle: '过往消费记录',
      stepSubtitle: '想如何处理老客户的消费历史？',

      optionForward: '从今日起计',
      optionForwardDescription: '之后的新交易才会累积积分',
      option3Months: '过去 3 个月',
      option6Months: '过去 6 个月',
      option12Months: '过去 12 个月',
      optionAll: '所有历史交易',
      recommended: '建议',

      previewLoading: '计算中…',
      previewLabel: '预计将处理：',
      previewOrders: '{count} 笔订单',
      previewAppointments: '{count} 个预约',
      previewMembers: '{count} 位客户将自动成为会员',
      previewPoints: '将发出 {count} 分',
      previewTierUpgrades: '{count} 位客户将升级',
      previewEmpty: '此范围内无符合条件的交易',

      confirmTitle: '确认回溯计分',
      confirmDescription: '一旦开始将无法撤销。客户下次接触时将看到会员身份。',
      confirmCta: '开始回溯',

      running: '回溯进行中…',
      completedTitle: '回溯完成',
      completedDescription: '已处理 {orders} 笔订单与 {appointments} 个预约，{members} 位客户加入会员，发出 {points} 分。',

      disclosure: '过往交易将根据当前累积比率换算为积分。客户下次接触时将看到会员身份。',
    },

    empty: {
      members: {
        title: '暂无会员',
        description: '客户开始累积积分后将在此显示。',
      },
      rewards: {
        title: '尚未设置奖励',
        description: '添加客户可用积分兑换的奖励。',
        cta: '添加奖励',
      },
      history: {
        title: '暂无活动记录',
        description: '累积、兑换与到期记录将在此显示。',
      },
    },

    errors: {
      insufficientPoints: '积分不足',
      notEnrolled: '尚未加入此计划',
      programInactive: '此计划目前未启用',
      backfillFailed: '回溯失败，请重试。',
    },

    reasons: {
      order_paid: '订单付款',
      order_completed: '订单完成',
      appointment_completed: '预约完成',
      signup_bonus: '入会奖励',
      birthday_bonus: '生日奖励',
      review_bonus: '评价奖励',
      referral_earn: '推荐奖励',
      tier_upgrade_bonus: '升级奖励',
      stamp_earned: '集印章',
      stamp_complete_bonus: '集印卡完成',
      manual_adjustment: '手动调整',
      bonus: '额外奖励',
      redemption_hold: '兑换预留',
      redemption_commit: '已兑换',
      redemption_cancel: '兑换取消',
      expiry: '积分到期',
      backfill_order: '过往订单',
      backfill_appointment: '过往预约',
    },
  },

  memberPortal: {
    signIn: {
      title: '登录会员账户',
      subtitle: '请输入你之前在该商户使用过的邮箱或电话，我们会发送一次性验证码。',
      contactLabel: '邮箱或电话号码',
      contactPlaceholder: 'you@example.com 或 +86 138 0000 0000',
      sendCode: '发送验证码',
      sending: '发送中…',
      contactInvalid: '请输入正确的邮箱或电话号码。',
      rateLimited: '请求过于频繁，请一小时后再试。',
      genericError: '出现错误，请重试。',
      privacyNote: '只有你之前提供给商户的邮箱／电话才能登录。',
      waVerify: {
        primaryCta: '使用 WhatsApp 继续',
        emailFallback: '改用邮箱',
        phoneLabel: '手机号码',
        phonePlaceholder: '+86 138 0000 0000',
        phoneInvalid: '请输入有效的手机号码。',
        initSubmit: '继续',
        initSubmitting: '准备中…',
        verifyTitle: '通过 WhatsApp 验证',
        verifySubtitle: '点击下方按钮，一键发送验证消息。消息送达后我们会自动为你登录。',
        tapToSend: '点此通过 WhatsApp 发送',
        qrLabel: '或用手机扫描下方 QR 码',
        expiresIn: '{seconds} 秒后过期',
        expired: '验证已过期，请重新开始。',
        mismatch: '你输入的电话与发送消息的 WhatsApp 账户不一致，请重试。',
        changePhone: '更换号码',
        waiting: '等待你的消息…',
        verified: '已验证 — 正在为你登录。',
      },
    },
    verify: {
      title: '输入验证码',
      subtitle: '我们已将 6 位数字验证码发送至 {contact}，5 分钟内有效。',
      codeLabel: '验证码',
      codePlaceholder: '123456',
      submit: '验证',
      submitting: '验证中…',
      resend: '重新发送',
      resendIn: '{seconds} 秒后可重新发送',
      invalidCode: '验证码不正确，请重试。',
      tooManyAttempts: '尝试次数过多，请重新申请。',
      edit: '使用其他联系方式',
    },
    dashboard: {
      welcomeBack: '欢迎回来，{name}',
      welcomeBackNoName: '欢迎回来',
      balanceLabel: '可用积分',
      lifetimeLabel: '累计积分',
      progressToNext: '距离 {name} 还差 {count} 分',
      atTopTier: '你已达到最高等级，感谢你长期支持。',
      yourRewardsHeader: '可兑换奖励',
      viewAllRewards: '查看全部奖励',
      recentActivityHeader: '最近活动',
      viewAllActivity: '查看全部记录',
      noRecentActivity: '暂无活动 — 你的积累与兑换记录将在此显示。',
    },
    rewards: {
      title: '奖励',
      emptyTitle: '暂无奖励',
      emptyDescription: '商户会持续添加奖励，请稍后再来查看。',
      pointsCost: '{points} 分',
      redeem: '兑换',
      redeeming: '处理中…',
      insufficient: '积分不足',
      confirmTitle: '确认兑换',
      confirmDescription: '使用 {points} 分兑换 {name}？兑换后会显示奖励编号，请在店内出示给店员。',
      confirmCta: '确认兑换',
      successTitle: '兑换成功',
      successDescription: '请在店内向店员出示以下编号：',
      voucherCodeLabel: '奖励编号',
      voucherExpiresLabel: '有效至 {date}',
      backToRewards: '返回奖励列表',
    },
    history: {
      title: '活动记录',
      filterAll: '全部',
      filterEarn: '累积',
      filterRedeem: '兑换',
      filterExpiry: '到期',
      emptyTitle: '暂无活动',
      emptyDescription: '你的累积、兑换、到期记录将在此显示。',
      loadMore: '加载更多',
    },
    settings: {
      title: '设置',
      languageSectionTitle: '语言',
      languageSectionHint: '仅影响此设备的显示，不影响商户端设置。',
      dataSectionTitle: '你的数据',
      exportData: '下载我的忠诚度数据',
      exportFormatJson: 'JSON',
      exportFormatCsv: 'CSV',
      exportHint: '完整的积累、兑换、到期记录副本，方便你自行保存。',
      accountSectionTitle: '账户',
      signOut: '退出登录',
      signingOut: '退出中…',
    },
    nav: {
      dashboard: '首页',
      bookings: '预约',
      orders: '订单',
      offers: '优惠',
      rewards: '奖励',
      history: '记录',
      settings: '设置',
    },
    offers: {
      title: '我的优惠',
      walletHeader: '你的优惠',
      publicHeader: '公开优惠',
      historyHeader: '已使用',
      emptyTitle: '暂时没有优惠',
      emptyDescription: '你的优惠会在这里显示。',
      claimTitle: '有优惠码？',
      claimPlaceholder: '输入优惠码',
      claimSubmit: '领取',
      claimSubmitting: '领取中…',
      claimSuccess: '已加入你的优惠夹',
      expiresLabel: '有效期至 {date}',
      noExpiry: '长期有效',
      minSpendLabel: '最低消费 {amount}',
      autoApplyTag: '自动套用',
      copyCode: '复制码',
      codeCopied: '已复制！',
      codeCopiedAnnouncement: '优惠码 {code} 已复制到剪贴板',
      useNow: '立即使用',
      useNowToastTitle: '优惠已套用',
      useNowToastDescription: '已加入购物车，结账即时生效。',
      identityHint: '已登入：{contact}',
      offerStatusAvailable: '可用',
      offerStatusReserved: '使用中',
      offerStatusUsed: '已用',
      offerStatusExpired: '已过期',
      offerStatusRevoked: '已撤回',
      discountPercent: '{value}% off',
      discountPercentCap: '{value}% off（最多减 {cap}）',
      discountFixed: '减 {value}',
      discountFreeShipping: '免运费',
      historySavedLabel: '已节省 {amount}',
      discountBogoFree: '买{n}送1',
      discountBogoPct: '买{n}件 最便宜那件{pct}% off',
      loyaltyHeader: '积分换领',
      loyaltyHint: '用积分换领的奖赏券 — 结账时可以使用。',
      loyaltyClaimedTag: '已挂在订单',
      loyaltyCounterTag: '到店使用',
    },
    bookings: {
      title: '我的预约',
      upcomingHeader: '即将到来',
      pastHeader: '历史',
      emptyTitle: '还没有预约',
      emptyDescription: '即将到来与历史预约都会在这里显示。',
      browseServices: '浏览服务',
      statusPending: '等待商户确认',
      statusConfirmed: '已确认',
      statusCompleted: '已完成',
      statusCancelled: '已取消',
      statusNoShow: '未到场',
      detail: {
        title: '预约详情',
        referenceLabel: '预约编号',
        backToList: '回到预约列表',
        notesLabel: '备注',
        depositLabel: '订金',
        manageHeader: '管理',
        cancelButton: '取消预约',
        cancelling: '取消中…',
        cancelConfirmTitle: '确定要取消？',
        cancelConfirmBody: '您即将取消预约 {reference}，无法恢复。',
        cancelConfirmCta: '是，取消',
        cancelKeep: '保留预约',
        cancelReasonLabel: '原因（选填）',
        cancelReasonPlaceholder: '想对商家说的话',
        cancelSuccess: '预约已取消。',
        cancelError: '无法取消，请再试一次。',
        outsideWindowError: '已超过取消时限，请直接联系商家。',
        contactShopToCancel: '此预约无法在网上取消，请联系商家办理。',
        cancelWindowNote: '预约前 {hours} 小时或更早取消可全额退款。',
        cancelDisabledNote: '此商家不接受在线取消，请直接联系。',
        withdrawRequestNote: '商家尚未确认此预约，你可以随时撤回申请。',
        cancelSuccessRefundLine: '依据取消政策，您将获退款 {pct}%。商家会于数个工作天内处理。',
        cancelSuccessNoRefund: '依据取消政策，此次取消不予退款。',
        cancelSuccessNoPolicy: '取消已记录。退款安排请联系商家确认。',
        cancelSuccessClose: '完成',
        verifyToManageNote: '验证手机后即可在此管理预约。',
        verifyToManageCta: '立即验证',
        contactMerchantHeader: '联系商家',
        contactWhatsApp: '打开 WhatsApp',
      },
    },
    orders: {
      title: '我的订单',
      activeHeader: '进行中',
      pastHeader: '历史',
      emptyTitle: '还没有订单',
      emptyDescription: '通过商家下单后，订单会显示在这里。',
      browseProducts: '浏览商品',
      itemsSummary: '{count} 件商品',
      statusPending: '待付款',
      statusConfirmed: '已确认',
      statusPaid: '已付款',
      statusShipped: '已发货',
      statusCompleted: '已完成',
      statusCancelled: '已取消',
      statusExpired: '已过期',
      statusRefunded: '已退款',
    },
    poweredBy: '由 NovixOne 提供',

    receiptCta: {
      title: '追踪你的积分',
      description: '随时查看积分、浏览奖励、自助获取奖励编号。',
      button: '进入会员主页',
    },

    voucherCommit: {
      title: '使用奖励',
      codeLabel: '奖励编号',
      codePlaceholder: 'ABC123',
      lookup: '查询',
      lookingUp: '查询中…',
      foundFor: '由 {name} 兑换',
      pointsHeld: '已预留 {points} 分',
      discountValue: '折扣面值 {value}',
      alreadyOnOrder: '此券已在结账时应用到订单，请勿再手动减价。',
      voucherExpires: '有效至 {date}',
      commitCta: '确认使用',
      committing: '处理中…',
      cancelCta: '取消',
      successTitle: '奖励已使用',
      successDescription: '积分已正式扣除，会员的奖励记录已更新。',
      errorNotFound: '找不到此奖励编号。',
      errorAlreadyUsed: '此奖励已使用过。',
      errorExpired: '此奖励已过期。',
      errorGeneric: '无法处理，请重试。',
    },
  },

  livechat: {
    launcher: {
      contactUs: '联系我们',
      needHelp: '需要帮助？',
    },
    picker: {
      title: '想怎样联系我们？',
      whatsapp: 'WhatsApp',
      whatsappSub: '用 WhatsApp 打开',
      line: 'LINE',
      lineSub: '用 LINE 打开',
      webChat: '在这里聊',
      webChatSubAnonymous: '直接在本页回复',
      webChatSubSignedIn: 'Web Chat（已登录）',
    },
    widget: {
      ariaLabel: '即时对话',
      closeAria: '关闭对话',
      loading: '加载中…',
      loadError: '无法加载对话。请刷新后重试。',
      poweredBy: 'Powered by NovixOne',
    },
    signin: {
      continueGoogle: '使用 Google 继续',
      continueApple: '使用 Apple 继续',
      continueEmail: '使用电邮继续',
      emailHint: '我们会用电邮发送一个 6 位验证码给你。',
      emailPlaceholder: 'you@example.com',
      sendCode: '发送验证码',
      sending: '发送中…',
      back: '← 返回',
      codeSentTo: '验证码已发送至 {contact}。请查收邮箱。',
      otpPlaceholder: '123456',
      verify: '验证',
      verifying: '验证中…',
      errorInvalidEmail: '请输入有效的电邮地址',
      errorSendFailed: '无法发送验证码，请重试。',
      errorWrongCode: '验证码错误，请重试。',
      errorCodeNotSixDigits: '验证码须为 6 位数字。',
      errorTooManyAttempts: '尝试次数过多，请重新发送。',
      errorCodeExpired: '验证码已过期，请重新发送。',
      errorVerifyFailed: '验证失败。',
    },
    conversation: {
      inputPlaceholder: '输入消息…',
      sendButton: '发送',
      sendingButton: '…',
      sendFailedSuffix: '（发送失败）',
      defaultGreeting: '您好！我可以协助查询预约、产品与订单。请选择一个方式登录即可开始对话。',
    },
  },
}

export default dict
